import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import "izitoast/dist/css/iziToast.min.css";
import Select from "react-select";
import "bootstrap/js/src/modal";
import useVendorOptions from "../hooks/useVendorOptions";
import { useVinDecoder } from "../hooks/useVinDecoder";
import { hydrateFromReceipt } from "./AddReceipts/hooks/useReceiptHydrator";
import ServicesPartsInlineTables from "./AddReceipts/ServicesPartsInlineTables";
import ReceiptEditReviewDialog from "./AddReceipts/ReceiptEditReviewDialog";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Typography,
  Checkbox,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ReusableFormDialog from "./FormModal/ReusableFormDialog";
import {
  buildCustomerPayload,
  createCustomerInitialValues,
  customerValidationSchema,
  getCustomerFormFields,
  prepareCustomerFormValues,
} from "./Customers/customerFormConfig";
import {
  buildVehicleCustomerOptions,
  buildVehiclePayload,
  createVehicleInitialValues,
  getVehicleFormFields,
  mapVinDecodedVehicle,
  prepareVehicleFormValues,
} from "./Vehicles/vehicleFormConfig";
import {
  partValidationSchema,
  tireValidationSchema,
  vehicleValidationSchema,
} from "./FormModal/validationSchemas";
import {
  buildTirePayload,
  createTireInitialValues,
  formatTireSizeInput,
  getTireFormFields,
  prepareTireFormValues,
} from "./Tires/tireFormConfig";
import {
  buildPartPayload,
  createPartInitialValues,
  getPartFormFields,
} from "./Parts/partFormConfig";
import {
  FULL_SERVICE_PRICE,
  calculatePaymentAmounts,
  computeReceiptTotals,
  computeReceiptWithFormattedTotals,
  isIncludedGroupServicePart,
  round2,
} from "../utils/receiptCalculations";

/* ─── Design tokens ─────────────────────────────────────── */
const TOKEN = {
  primary: "#36589A",
  primaryHover: "#2E4D88",
  headerGradient: "linear-gradient(135deg, #36589A 0%, #2E4D88 100%)",
  pageBg: "#F8FAFC",
  cardBg: "#FFFFFF",
  cardBorder: "1px solid #E5E7EB",
  cardRadius: "12px",
  cardShadow: "0 1px 3px rgba(0,0,0,0.08)",
  inputBorder: "#D1D5DB",
  labelColor: "#1F2937",
  inputColor: "#374151",
  sectionHeaderColor: "#36589A",
  breadcrumbColor: "#6B7280",
  fontFamily: 'Inter, "Segoe UI", sans-serif',
};

const styles = {
  /* ── Outer wrapper: clears fixed navbar + sticky footer ── */
  pageWrapper: {
    minHeight: "100vh",
    paddingTop: 72, // matches PageShell topSpacing (md:12 = 96px)
    display: "flex",
    flexDirection: "column",
    backgroundColor: TOKEN.pageBg,
    fontFamily: TOKEN.fontFamily,
    boxSizing: "border-box",
  },

  /* ── Horizontal container matching PageShell px:{xs:2,sm:3,md:4} ── */
  container: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "0 32px", // ~md:4 = 32px; PageShell uses Container px
    boxSizing: "border-box",
  },

  /* ── Header Paper: matches PageShell's gradient Paper ── */
  header: {
    marginBottom: 16,
    paddingTop: 8,
  },

  headerTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: "#FFFFFF",
    fontFamily: TOKEN.fontFamily,
    margin: 0,
  },
  headerSubtitle: {
    fontSize: 13,
    color: "rgba(255,255,255,0.75)",
    marginTop: 2,
    marginBottom: 0,
  },
  backBtn: {
    fontSize: "16px",
    color: TOKEN.breadcrumbColor,
    border: "none",
    cursor: "pointer",
    background: "transparent",
    fontWeight: 400,
    "&:hover": {
      color: "#36589A",
      fontWeight: 700,
    },
  },
  /* ── Breadcrumb row inside header Paper ── */
  breadcrumb: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    color: TOKEN.breadcrumbColor,
    marginBottom: 10,
    "&:hover": {
      color: "#36589A",
      fontWeight: 700,
    },
  },
  breadcrumbActive: {
    color: TOKEN.primary,
    fontWeight: 600,
  },
  actionRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  /* ── Title row inside header Paper ── */
  headerTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    position: "relative",
    zIndex: 1,
  },

  /* ── Body: two-column layout below header ── */
  body: {
    display: "flex",
    gap: 16,
    alignItems: "flex-start",
    flex: 1,
    minWidth: 0,
    border: TOKEN.cardBorder,
    borderRadius: "8px",
    boxShadow: TOKEN.cardShadow,
    padding: "16px",
  },
  leftCol: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  rightCol: {
    width: 280,
    flexShrink: 0,
    position: "sticky",
    top: 80,
    maxHeight: "calc(100vh - 96px)",
    overflowY: "auto",
    marginLeft: 16,
  },

  card: {
    background: TOKEN.cardBg,
    border: TOKEN.cardBorder,
    borderRadius: "8px",
    boxShadow: TOKEN.cardShadow,
    padding: "16px",
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  actionFooter: {
    position: "sticky",
    bottom: 60,
    left: 0,
    right: 0,
    background: "#FFFFFF",
    borderTop: "1px solid #E5E7EB",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    gap: 12,
    zIndex: 200,
    boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
    willChange: "transform",
  },
  sectionHeader: {
    fontSize: 16,
    fontWeight: 600,
    color: TOKEN.sectionHeaderColor,
    fontFamily: TOKEN.fontFamily,
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
  },
  sectionIcon: {
    width: 20,
    height: 20,
    color: TOKEN.primary,
  },
  label: {
    fontSize: 14,
    fontWeight: 600,
    color: TOKEN.labelColor,
    marginBottom: 4,
    display: "block",
  },
  input: {
    fontSize: 14,
    fontWeight: 400,
    color: TOKEN.inputColor,
    border: `1px solid ${TOKEN.inputBorder}`,
    borderRadius: 8,
    padding: "9px 12px",
    width: "100%",
    outline: "none",
    fontFamily: TOKEN.fontFamily,
    backgroundColor: "#fff",
    boxSizing: "border-box",
  },
  textarea: {
    fontSize: 14,
    fontWeight: 400,
    color: TOKEN.inputColor,
    border: `1px solid ${TOKEN.inputBorder}`,
    borderRadius: 8,
    padding: "9px 12px",
    width: "100%",
    outline: "none",
    fontFamily: TOKEN.fontFamily,
    backgroundColor: "#fff",
    resize: "vertical",
    boxSizing: "border-box",
  },
  primaryBtn: {
    background: TOKEN.primary,
    color: "#FFFFFF",
    border: "none",
    borderRadius: 8,
    padding: "10px 20px",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: TOKEN.fontFamily,
    display: "flex",
    alignItems: "center",
    gap: 6,
    transition: "background 0.15s",
  },
  secondaryBtn: {
    background: "#FFFFFF",
    color: TOKEN.primary,
    border: `1px solid ${TOKEN.primary}`,
    borderRadius: 8,
    padding: "9px 16px",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: TOKEN.fontFamily,
    transition: "background 0.15s",
  },
  outlineBtn: {
    background: "#FFFFFF",
    color: TOKEN.primary,
    border: `1px solid ${TOKEN.primary}`,
    borderRadius: 8,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: TOKEN.fontFamily,
    whiteSpace: "nowrap",
  },
  dangerBtn: {
    background: "#DC2626",
    color: "#FFFFFF",
    border: "none",
    borderRadius: 8,
    padding: "10px 20px",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: TOKEN.fontFamily,
  },
  ghostBtn: {
    background: "transparent",
    color: TOKEN.breadcrumbColor,
    border: "1px solid #D1D5DB",
    borderRadius: 8,
    padding: "10px 20px",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: TOKEN.fontFamily,
  },
  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
  },
  formRow: {
    marginBottom: 16,
  },
  errorText: {
    fontSize: 12,
    color: "#DC2626",
    marginTop: 4,
    display: "block",
  },
  divider: {
    border: "none",
    borderTop: "1px solid #E5E7EB",
    margin: "16px 0",
  },
  summaryCard: {
    background: TOKEN.cardBg,
    border: TOKEN.cardBorder,
    borderRadius: TOKEN.cardRadius,
    boxShadow: TOKEN.cardShadow,
    overflow: "hidden",
  },
  summaryHeader: {
    background: TOKEN.headerGradient,
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  summaryHeaderTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: "#FFFFFF",
    fontFamily: TOKEN.fontFamily,
  },
  summaryBody: {
    padding: "16px 20px",
  },
  summaryLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: TOKEN.breadcrumbColor,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    marginBottom: 2,
  },
  summaryValue: {
    fontSize: 14,
    fontWeight: 600,
    color: TOKEN.labelColor,
  },
  summaryMeta: {
    fontSize: 12,
    color: TOKEN.breadcrumbColor,
    marginTop: 1,
  },
  lineRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "8px 0",
    borderBottom: "1px solid #F3F4F6",
    gap: 8,
  },
  lineLabel: {
    fontSize: 13,
    color: TOKEN.inputColor,
    flex: 1,
    minWidth: 0,
  },
  lineAmount: {
    fontSize: 13,
    fontWeight: 600,
    color: TOKEN.labelColor,
    whiteSpace: "nowrap",
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "12px 0 4px",
    borderTop: "2px solid #E5E7EB",
    marginTop: 4,
  },
  totalLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: TOKEN.labelColor,
  },
  totalAmount: {
    fontSize: 28,
    fontWeight: 700,
    color: TOKEN.primary,
    fontFamily: TOKEN.fontFamily,
    lineHeight: 1,
  },
  actionCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    padding: "14px 12px",
    background: "#F8FAFC",
    border: `1px solid ${TOKEN.inputBorder}`,
    borderRadius: 10,
    cursor: "pointer",
    flex: 1,
    transition: "border-color 0.15s, background 0.15s",
    fontFamily: TOKEN.fontFamily,
  },
  actionCardLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: TOKEN.primary,
  },
  actionCardSub: {
    fontSize: 11,
    color: TOKEN.breadcrumbColor,
  },
};

/* ─── Shared select styles ───────────────────────────────── */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? TOKEN.primary : TOKEN.inputBorder,
    boxShadow: state.isFocused ? `0 0 0 2px ${TOKEN.primary}22` : "none",
    borderRadius: 8,
    fontSize: 14,
    color: TOKEN.inputColor,
    fontFamily: TOKEN.fontFamily,
    minHeight: 38,
    "&:hover": { borderColor: TOKEN.primary },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected
      ? TOKEN.primary
      : state.isFocused
        ? `${TOKEN.primary}14`
        : "transparent",
    color: state.isSelected ? "#fff" : TOKEN.inputColor,
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: `${TOKEN.primary}18`,
    borderRadius: 6,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: TOKEN.primary,
    fontWeight: 600,
    fontSize: 12,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: TOKEN.primary,
    ":hover": { backgroundColor: TOKEN.primary, color: "#fff" },
  }),
};

const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

const formatPhone = (value) => {
  const digits = String(value || "")
    .replace(/\D/g, "")
    .slice(0, 10);
  if (digits.length <= 3) return digits ? `(${digits}` : "";
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const buildCustomerOption = (customer) =>
  customer
    ? {
        label:
          `${customer.name || ""}${customer.phone ? ` ${formatPhone(customer.phone)}` : ""}`.trim(),
        value: customer,
      }
    : null;

const formatVehicleLabel = (vehicle = {}) => {
  const baseLabel = [
    vehicle?.year,
    vehicle?.make || vehicle?.name,
    vehicle?.model,
  ]
    .filter(Boolean)
    .join(" ");
  const vin = String(vehicle?.vin_number || "")
    .replace(/\s/g, "")
    .toUpperCase();
  const vinSuffix = vin ? vin.slice(-8) : "";
  return [baseLabel, vinSuffix].filter(Boolean).join(" · ") || "Vehicle";
};

const buildVehicleOptions = (list = []) =>
  (Array.isArray(list) ? list : []).map((vehicleItem) => ({
    label: formatVehicleLabel(vehicleItem),
    value: vehicleItem,
  }));

const createServiceLineItemId = () =>
  `svc-line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const ensureServiceLineItemIds = (options = []) =>
  (Array.isArray(options) ? options : []).map((option) => {
    if (!option?.value) return option;
    const linkedParts = Array.isArray(option.value.linkedParts)
      ? option.value.linkedParts.map((part) => ({
          ...part,
          linkedPartId:
            part?.linkedPartId ||
            `linked-part-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        }))
      : [];
    return {
      ...option,
      value: {
        ...option.value,
        lineItemId: option.value.lineItemId || createServiceLineItemId(),
        linkedParts,
      },
    };
  });

function AddReceipts({ receiptToEdit } = {}) {
  const { draftId } = useParams();
  const isDateOnlyString = (value) =>
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const formatDateParts = (value) => {
    if (!isDateOnlyString(value)) return null;
    const [year, month, day] = value.split("-");
    return { year, month, day };
  };

  const [footerOffset, setFooterOffset] = useState(60);

  useEffect(() => {
    const footerEl = document.querySelector("[data-app-footer]"); // swap for your real footer's selector
    if (!footerEl) return;
    const update = () =>
      setFooterOffset(footerEl.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(footerEl);
    return () => ro.disconnect();
  }, []);

  const toLocalDateInput = (value = new Date()) => {
    if (isDateOnlyString(value)) return value;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  let receiptInit = {
    date: toLocalDateInput(),
    customer: "",
    vehicle: "",
    miles: "",
    services: [],
    tires: [],
    tiresQuantity: 0,
    tiresPrice: 0,
    oil: "",
    oilQuantity: 0,
    extraOilQuantity: 0,
    extraOilPrice: 0,
    oilPrice: 0,
    oilFilter: "",
    oilFilterQuantity: 1,
    oilFilterPrice: 0,
    fullServiceIncluded: false,
    fullServicePrice: 0,
    oilTechnician: [],
    extraOilSelection: null,
    extraOilUnitPrice: 0,
    oilChangeAdded: false,
    techNotes: "",
    totalPrice: 0,
    paid: 0,
    remaining: 0,
    status: "",
    paymentType: "",
    taxIncluded: false,
    taxType: "",
    tax: 0,
    discountIncluded: false,
    discountType: "",
    discount: 0,
    user_id: Helpers.authParentId,
    receiptType: "Quote",
  };

  let customerInit = createCustomerInitialValues(Helpers.authParentId);
  let vehicleInit = createVehicleInitialValues(Helpers.authParentId);

  const paymentTypeOptions = useMemo(
    () => [
      { label: "Cash", value: "Cash" },
      { label: "Card", value: "Card" },
      { label: "Check", value: "Check" },
      { label: "Online", value: "Online" },
    ],
    [],
  );

  const statusOptions = useMemo(
    () => [
      { label: "Unpaid", value: "Unpaid" },
      { label: "Partialy Paid", value: "Partialy Paid" },
      { label: "Paid", value: "Paid" },
    ],
    [],
  );

  let [selectedCustomer, setSelectedCustomer] = useState();
  let [selectedVehicle, setSelectedVehicle] = useState();
  let [selectedServices, setSelectedServices] = useState([]);
  let [, setSelectedTires] = useState([]);
  let [selectedOil, setSelectedOil] = useState([]);
  const [oilChangeDraft, setOilChangeDraft] = useState(null);
  let [, setselectedStatus] = useState("");
  let [selectedPaymentType, setSelectedPaymentType] = useState("");
  const todayStr = toLocalDateInput();
  const [payment, setPayment] = useState({ payment_date: todayStr });
  const [paymentErrors, setPaymentErrors] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [draftReceiptId, setDraftReceiptId] = useState(null);
  const [paymentSaving, setPaymentSaving] = useState(false);

  let [customerOptions, setCustomersOptions] = useState([]);
  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  let [vehiclesOptions, setVehiclesOptions] = useState([]);
  const [vehicleSearchLoading, setVehicleSearchLoading] = useState(false);
  const [vehicleSearchInput, setVehicleSearchInput] = useState("");
  let [serviceOptions, setServiceOptions] = useState([]);
  let [partsOptions, setPartsOptions] = useState([]);
  const [nonInventoryPartsOptions, setNonInventoryPartsOptions] = useState([]);
  const [nonInventoryPartsLoading, setNonInventoryPartsLoading] = useState(true);
  const [, setPartSearchInput] = useState("");
  const [partSearchLoading, setPartSearchLoading] = useState(false);
  let [oilsOptions, setOilsOptions] = useState([]);
  const [filtersOptions, setFiltersOptions] = useState([]);
  const [selectedOilFilter, setSelectedOilFilter] = useState(null);
  const [selectedExtraOil, setSelectedExtraOil] = useState(null);
  const [extraOilQty, setExtraOilQty] = useState(1);
  const [extraOilUnitPrice, setExtraOilUnitPrice] = useState(0);
  const [oilTechnician, setOilTechnician] = useState([]);

  const [btnLoading, setBtnLoading] = useState(false);
  const [customerBtnLoading, setCustomerBtnLoading] = useState(false);
  const [vehicleBtnLoading, setVehicleBtnLoading] = useState(false);
  const [tireBtnLoading, setTireBtnLoading] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [loadedEditPrefill, setLoadedEditPrefill] = useState(false);
  const [showEditReview, setShowEditReview] = useState(false);
  const [pendingEditMode, setPendingEditMode] = useState("estimate");
  const [editSaveError, setEditSaveError] = useState("");
  const [loadedVehiclePrefill, setLoadedVehiclePrefill] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState(customerInit);

  const [, setServices] = useState([]);

  const [, setTires] = useState([]);
  const [, setOils] = useState([]);

  const [, setTechnicians] = useState([]);
  const [techniciansOptions, setTechniciansOptions] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vehicle, setVehicle] = useState(vehicleInit);
  const [lastMiles, setLastMiles] = useState(null);
  const [onlyFillEmptyVehicleVin, setOnlyFillEmptyVehicleVin] = useState(true);
  const vehicleFormikValuesRef = useRef({});
  const vehicleFormikSetFieldValueRef = useRef(null);
  const lastDecodedVinRef = useRef("");
  const lastDecodedVehicleRef = useRef(null);

  const [receipt, setReceipt] = useState(receiptInit);
  const [errors, setErrors] = useState({});
  const [taxValue, setTaxValue] = useState([]);
  const resolvedTaxRate = Number(taxValue?.tax) || 0;

  const [isCustomerModelVisible, setIsCustomerModelVisible] = useState(false);
  const [customerModalMode, setCustomerModalMode] = useState("add");
  const [isVehicleModelVisible, setIsVehicleModelVisible] = useState(false);
  const [servicesPartsTab, setServicesPartsTab] = useState("services");
  const [servicesPartsEditingKey, setServicesPartsEditingKey] = useState(null);
  const [isOilChangeModalVisible, setIsOilChangeModalVisible] = useState(false);
  const [isServicesModalOpen, setIsServicesModalOpen] = useState(false);
  const [isAddTireModalVisible, setIsAddTireModalVisible] = useState(false);

  const [, setSelectedPart] = useState(null);
  const [, setPartQty] = useState(1);
  const [, setPartUnitPrice] = useState(0);
  const [, setPartDescription] = useState("");
  const [isAddPartModalVisible, setIsAddPartModalVisible] = useState(false);
  const [partForm, setPartForm] = useState(
    createPartInitialValues(Helpers.authParentId, "Non Inventory"),
  );
  const [partFormLoading] = useState(false);

  const [tireForm, setTireForm] = useState(
    createTireInitialValues(Helpers.authParentId),
  );
  const [, setIsTireModalVisible] = useState(false);
  const [, setSelectedTireOptions] = useState([]);
  const [, setPendingTire] = useState(null);
  const [rawTires, setRawTires] = useState([]);
  const [loadedDraftSession, setLoadedDraftSession] = useState(false);

  const draftSaveTimeoutRef = useRef(null);
  const draftBootstrapStartedRef = useRef(false);
  const vehicleSearchTimeoutRef = useRef(null);
  const vehicleSearchRequestIdRef = useRef(0);
  const customerSearchTimeoutRef = useRef(null);
  const customerSearchRequestIdRef = useRef(0);
  const partSearchTimeoutRef = useRef(null);
  const partSearchRequestIdRef = useRef(0);

  let navigate = useNavigate();
  const location = useLocation();
  const incomingEditReceipt = receiptToEdit || location.state?.receiptToEdit;
  const editingInvoice = isEditing && incomingEditReceipt?.receiptType === "Invoice";
  const { getVendorFieldOptions } = useVendorOptions(navigate);

  const formatSelectedServiceLabel = (item) => {
    const value = item?.value ?? item ?? {};
    const name = value?.name || item?.label || "Service";
    const total = round2(
      Number(value?.total_price) ||
        Number(value?.price || 0) * Number(value?.quantity || 0),
    ).toFixed(2);
    const linkedPartsCount = Array.isArray(value?.linkedParts)
      ? value.linkedParts.reduce(
          (sum, part) =>
            sum + (Number(part?.quantity) > 0 ? Number(part.quantity) : 1),
          0,
        )
      : 0;
    const suffix =
      value?.serviceType === "Group" && linkedPartsCount > 0
        ? ` · ${linkedPartsCount} linked`
        : "";
    return `${name} ( $${total} )${suffix}`;
  };
  const getServicePriority = (service) => {
    const priority = Number(service?.priority);
    return Number.isFinite(priority) && priority > 0
      ? priority
      : Number.MAX_SAFE_INTEGER;
  };
  const sortServiceRecords = (services = []) =>
    [...services].sort(
      (a, b) =>
        getServicePriority(a) - getServicePriority(b) ||
        String(a?.service_name || a?.name || "").localeCompare(
          String(b?.service_name || b?.name || ""),
          undefined,
          { sensitivity: "base" },
        ),
    );
  const buildServiceOption = (record = {}) => {
    const service = {
      _id: record._id,
      name: record.service_name || record.name,
      price: record.price,
      description: record.description,
      quantity: 1,
      total_price: record.price,
      tax: record.tax,
      priority: record.priority,
      serviceType: record.serviceType || "Standard",
      groupCategory: record.groupCategory || "",
      linkedParts: Array.isArray(record.linkedParts) ? record.linkedParts : [],
      type: "service",
    };
    return {
      label: formatSelectedServiceLabel({ value: service }),
      value: service,
    };
  };
  const formatPartOptionLabel = (option, { context }) => {
    const part = option?.value || {};
    const name = part?.name || option?.label || "Unnamed Part";
    const partNumber = part?.partNumber || "No part #";
    const quantity = Number(part?.quantity || 0);
    const sellPrice = formatMoney(part?.sellPrice || 0);
    const description = String(part?.description || "").trim();
    const isSpecialOrder = (part?.partType || "Inventory") === "Non Inventory";
    const specialOrderStatus = String(part?.specialOrderStatus || "Available");

    if (context === "value") {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            py: 0.25,
          }}
        >
          <Typography sx={{ fontWeight: 600 }} noWrap>
            {name}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
            #{partNumber}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
            Qty {quantity}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "#0f766e", fontWeight: 700 }}
            noWrap
          >
            {sellPrice}
          </Typography>
          {isSpecialOrder ? (
            <Typography
              variant="caption"
              sx={{ color: "#7c3aed", fontWeight: 600 }}
              noWrap
            >
              {specialOrderStatus}
            </Typography>
          ) : null}
        </Box>
      );
    }

    return (
      <Box sx={{ py: 0.25 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Typography
            sx={{ fontWeight: 600, color: "#111827", minWidth: 0, flex: 1 }}
            noWrap
          >
            {name}
          </Typography>
          {description ? (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", maxWidth: "45%", flexShrink: 0 }}
              noWrap
            >
              {description}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 0.5 }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Part #: {partNumber}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Qty: {quantity}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "#0f766e", fontWeight: 700 }}
          >
            Sell: {sellPrice}
          </Typography>
          {isSpecialOrder ? (
            <Typography
              variant="caption"
              sx={{ color: "#7c3aed", fontWeight: 600 }}
            >
              Status: {specialOrderStatus}
            </Typography>
          ) : null}
        </Box>
      </Box>
    );
  };
  const buildPartCatalogOption = (part = {}) => ({
    label: `${part.name || "Unnamed Part"}${part.partNumber ? ` (${part.partNumber})` : ""}`,
    value: part,
  });
  const isAvailableInventoryPart = (part = {}) =>
    (part.partType || "Inventory") !== "Non Inventory" &&
    Number(part.quantity || 0) > 0;
  const sortPartsByName = (parts = []) =>
    [...parts].sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
        sensitivity: "base",
        numeric: true,
      }),
    );
  const getServiceLineKey = (serviceOption) =>
    serviceOption?.value?.lineItemId ||
    serviceOption?.lineItemId ||
    serviceOption?.value?._id ||
    serviceOption?.value?.id;

  const cloneServiceOptionForEstimate = (serviceOption) => {
    if (!serviceOption?.value) return serviceOption;
    const value = serviceOption.value;
    return {
      ...serviceOption,
      value: {
        ...value,
        lineItemId: value.lineItemId || createServiceLineItemId(),
        technicians: Array.isArray(value.technicians)
          ? [...value.technicians]
          : value.technician
            ? [value.technician]
            : [],
        linkedParts: Array.isArray(value.linkedParts)
          ? value.linkedParts.map((part) => ({
              ...part,
              linkedPartId:
                part?.linkedPartId ||
                `linked-part-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            }))
          : [],
      },
    };
  };
  const createCustomServiceOption = ({ serviceType = "Standard" } = {}) => {
    const id = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const value = {
      _id: id,
      id,
      lineItemId: createServiceLineItemId(),
      name: "",
      price: 0,
      description: "",
      quantity: 1,
      total_price: 0,
      tax: "Taxable",
      type: "custom_service",
      isCustom: true,
      serviceType,
      groupCategory: "",
      linkedParts: [],
    };
    return { label: formatSelectedServiceLabel({ value }), value };
  };

  const createGroupServicePartOptions = (serviceOption) => {
    const service = serviceOption?.value || {};
    if (service.serviceType !== "Group" || !service.lineItemId) return [];

    const templates =
      Array.isArray(service.linkedParts) && service.linkedParts.length
        ? service.linkedParts
        : [{}];
    const servicePrice = Number(service.price) || 0;

    return templates.map((template) => {
      const quantity = Math.max(1, Number(template.quantity) || 1);
      const usesGroupServiceName = !String(template.name || "").trim();
      const name = usesGroupServiceName ? service.name || "" : template.name;
      const isInventoryPart = Boolean(template._id || template.id);
      const quoteOnlyPartId = isInventoryPart
        ? ""
        : `quote-part-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const value = {
        lineItemId: createServiceLineItemId(),
        ...getReceiptPartFields(template, servicePrice),
        name,
        description: template.description || "",
        price: servicePrice,
        sellPrice: servicePrice,
        quantity,
        total_price: 0,
        tax: template.tax || "Taxable",
        type: "part",
        partType: isInventoryPart ? "Inventory" : "Non Inventory",
        ...(isInventoryPart
          ? {}
          : {
              quoteOnlyPart: true,
              quoteOnlyPartId,
              specialOrderStatus: "Quote Only",
            }),
        includedInGroupService: true,
        groupServicePriceOverride: true,
        relatedServiceLineId: service.lineItemId,
        usesGroupServiceName,
      };

      return {
        label: name || "Group Service Part",
        value,
      };
    });
  };

  const handleSaveOilChange = () => {
    setOilChangeDraft(null);
    setIsOilChangeModalVisible(false);
  };
  const handleCancelOilChange = () => {
    if (oilChangeDraft) {
      // restore receipt oil fields
      updateReceiptTotals({
        oil: oilChangeDraft.oil,
        oilPrice: oilChangeDraft.oilPrice,
        oilQuantity: oilChangeDraft.oilQuantity,
        extraOilSelection: oilChangeDraft.extraOilSelection,
        extraOilPrice: oilChangeDraft.extraOilPrice,
        extraOilQuantity: oilChangeDraft.extraOilQuantity,
        extraOilUnitPrice: oilChangeDraft.extraOilUnitPrice,
        oilFilter: oilChangeDraft.oilFilter,
        oilFilterPrice: oilChangeDraft.oilFilterPrice,
        oilFilterQuantity: oilChangeDraft.oilFilterQuantity,
        fullServiceIncluded: oilChangeDraft.fullServiceIncluded,
        fullServicePrice: oilChangeDraft.fullServicePrice,
        oilTechnician: oilChangeDraft.oilTechnician,
      });
      // restore local select states
      setSelectedOil(oilChangeDraft._selectedOil);
      setSelectedOilFilter(oilChangeDraft._selectedOilFilter);
      setSelectedExtraOil(oilChangeDraft._selectedExtraOil);
      setExtraOilQty(oilChangeDraft._extraOilQty);
      setExtraOilUnitPrice(oilChangeDraft._extraOilUnitPrice);
      setOilTechnician(oilChangeDraft._oilTechnician);
    }
    setOilChangeDraft(null);
    setIsOilChangeModalVisible(false);
  };

  const createCustomServiceTemplateOption = () => ({
    label: "Custom Service",
    value: {
      id: "custom-service-template",
      _id: "custom-service-template",
      name: "Custom Service",
      isCustom: true,
      isCustomTemplate: true,
    },
  });

  const recomputeServices = (nextSelected) => {
    setReceipt((prev) => {
      const { services, subtotal, tax, totalPrice, remaining } =
        computeReceiptTotals({
          services: nextSelected,
          tires: prev.tires || [],
          oilPrice: prev.oilPrice,
          extraOilPrice: prev.extraOilPrice,
          oilFilterPrice: prev.oilFilterPrice,
          fullServiceIncluded: prev.fullServiceIncluded,
          fullServicePrice: prev.fullServicePrice,
          taxIncluded: prev.taxIncluded,
          taxPct: prev.taxType,
          discountIncluded: prev.discountIncluded,
          discountType: prev.discountType,
          discount: prev.discount,
          paid: prev.paid,
        });
      return {
        ...prev,
        services,
        subTotal: subtotal.toFixed(2),
        tax: tax.toFixed(2),
        totalPrice: totalPrice.toFixed(2),
        remaining: remaining.toFixed(2),
      };
    });
  };

  const handleAddCatalogService = (option) => {
    if (!option?.value) return;
    const clonedOption = cloneServiceOptionForEstimate(option);
    const generatedParts = createGroupServicePartOptions(clonedOption);
    const nextOption = generatedParts.length
      ? {
          ...clonedOption,
          value: { ...clonedOption.value, linkedParts: [] },
        }
      : clonedOption;
    const nextSelected = [
      ...(selectedServices || []),
      nextOption,
      ...generatedParts,
    ];
    setSelectedServices(nextSelected);
    recomputeServices(nextSelected);
    return nextOption;
  };

  const handleAddBlankService = (options) => {
    const nextOption = createCustomServiceOption(options);
    const generatedParts = createGroupServicePartOptions(nextOption);
    const nextSelected = [
      ...(selectedServices || []),
      nextOption,
      ...generatedParts,
    ];
    setSelectedServices(nextSelected);
    recomputeServices(nextSelected);
    return nextOption;
  };

  const handleUpdateServiceLine = (lineItemId, patch) => {
    const currentLine = (selectedServices || []).find(
      (opt) => getServiceLineKey(opt) === lineItemId,
    );
    const currentValue = currentLine?.value || {};
    const isGroupService = currentValue.serviceType === "Group";

    const nextSelected = (selectedServices || []).map((opt) => {
      if (getServiceLineKey(opt) === lineItemId) {
        const nextValue = { ...opt.value, ...patch };
        if ("price" in patch || "quantity" in patch) {
          const price = Number(patch.price ?? opt.value.price) || 0;
          const quantity = Number(patch.quantity ?? opt.value.quantity) || 0;
          nextValue.total_price = nextValue.includedInGroupService
            ? 0
            : price * quantity;
        }
        if (
          nextValue.includedInGroupService &&
          "name" in patch &&
          patch.name !== opt.value.name
        ) {
          nextValue.usesGroupServiceName = false;
        }
        return {
          ...opt,
          label: formatSelectedServiceLabel({ value: nextValue }),
          value: nextValue,
        };
      }

      if (
        !isGroupService ||
        opt.value?.relatedServiceLineId !== lineItemId ||
        !opt.value?.groupServicePriceOverride
      ) {
        return opt;
      }

      const nextValue = { ...opt.value, total_price: 0 };
      if ("price" in patch) {
        const servicePrice = Number(patch.price) || 0;
        nextValue.price = servicePrice;
        nextValue.sellPrice = servicePrice;
      }
      if ("name" in patch && nextValue.usesGroupServiceName) {
        nextValue.name = patch.name;
      }

      return {
        ...opt,
        label: formatSelectedServiceLabel({ value: nextValue }),
        value: nextValue,
      };
    });
    setSelectedServices(nextSelected);
    recomputeServices(nextSelected);
  };

  const handleRemoveServiceLine = (lineItemId) => {
    const removedLine = (selectedServices || []).find(
      (opt) => getServiceLineKey(opt) === lineItemId,
    );
    const removeGeneratedParts = removedLine?.value?.serviceType === "Group";
    const nextSelected = (selectedServices || []).filter(
      (opt) =>
        getServiceLineKey(opt) !== lineItemId &&
        (!removeGeneratedParts || opt.value?.relatedServiceLineId !== lineItemId),
    );
    setSelectedServices(nextSelected);
    recomputeServices(nextSelected);
  };

  const handleAddCatalogPart = (option) => {
    if (!option?.value) return;
    const part = option.value;
    const price = Number(part.sellPrice || 0);
    const service = {
      lineItemId: createServiceLineItemId(),
      ...(part._id ? { _id: part._id } : {}),
      name: part.name,
      price,
      description:
        part.description || (part.partNumber ? `Part #${part.partNumber}` : ""),
      quantity: 1,
      total_price: price,
      tax: "Taxable",
      type: "part",
      ...getReceiptPartFields(part, price),
    };
    const nextSelected = [
      ...(selectedServices || []),
      {
        label: `${part.name}${part.partNumber ? ` (${part.partNumber})` : ""}`,
        value: service,
      },
    ];
    setSelectedServices(nextSelected);
    recomputeServices(nextSelected);
    return nextSelected[nextSelected.length - 1];
  };

  const handleAddBlankPart = (overrides = {}) => {
    const quoteOnlyPartId = `quote-part-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const service = {
      lineItemId: createServiceLineItemId(),
      name: "",
      price: 0,
      description: "",
      quantity: 1,
      total_price: 0,
      tax: "Taxable",
      type: "part",
      partType: "Non Inventory",
      quoteOnlyPart: true,
      quoteOnlyPartId,
      specialOrderStatus: "Quote Only",
      ...overrides,
    };
    const nextSelected = [
      ...(selectedServices || []),
      { label: "New Part", value: service },
    ];
    setSelectedServices(nextSelected);
    recomputeServices(nextSelected);
    return nextSelected[nextSelected.length - 1];
  };
  const handleAddCatalogTire = (option) => {
    if (!option?.value) return;
    const base = option.value;
    const id = base._id || base.id;
    const alreadyAdded = (receipt.tires || []).some((t) => t.id === id);
    if (alreadyAdded) return;
    const price = Number(base.price) || 0;
    const newTire = {
      id,
      condition: base.condition || base.quality || "Used",
      brand: base.brand || "Unknown",
      size: formatTireSizeInput(base.size || ""),
      description: base.description || "",
      price,
      quantity: 1,
      total_price: price,
      tax: base.tax || "Taxable",
      type: "tire_service",
      technician: null,
      available: Number(base.quantity) || 0,
    };
    updateReceiptTotals({ tires: [...(receipt.tires || []), newTire] });
    return newTire;
  };

  const handleUpdateTireLine = (id, patch) => {
    const nextTires = (receipt.tires || []).map((t) =>
      t.id === id ? { ...t, ...patch } : t,
    );
    updateReceiptTotals({ tires: nextTires });
  };

  const handleRemoveTireLine = (id) => {
    const nextTires = (receipt.tires || []).filter((t) => t.id !== id);
    updateReceiptTotals({ tires: nextTires });
  };

  const handleAddBlankTire = () => {
    const id = `blank-tire-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const newTire = {
      id,
      condition: "New",
      brand: "",
      size: "",
      description: "",
      price: 0,
      quantity: 1,
      total_price: 0,
      tax: "Taxable",
      type: "tire_service",
      technician: null,
      available: 0,
      isBlank: true,
    };
    updateReceiptTotals({ tires: [...(receipt.tires || []), newTire] });
    return newTire;
  };
  const updateReceiptTotals = (patch = {}) => {
    setReceipt((prev) => {
      const next = { ...prev, ...patch };
      const {
        subtotal,
        discountAmount,
        tax,
        totalPrice,
        remaining,
        services,
        tires,
      } = computeReceiptTotals({
        services: next.services || [],
        tires: next.tires || [],
        oilPrice: next.oilPrice,
        extraOilPrice: next.extraOilPrice,
        oilFilterPrice: next.oilFilterPrice,
        fullServiceIncluded: next.fullServiceIncluded,
        fullServicePrice: next.fullServicePrice,
        taxIncluded: next.taxIncluded,
        taxPct: next.taxType,
        discountIncluded: next.discountIncluded,
        discountType: next.discountType,
        discount: next.discount,
        paid: next.paid,
      });
      return {
        ...next,
        services,
        tires,
        subTotal: subtotal.toFixed(2),
        subtotal: subtotal.toFixed(2),
        discountAmount: discountAmount.toFixed(2),
        tax: tax.toFixed(2),
        totalPrice: totalPrice.toFixed(2),
        remaining: remaining.toFixed(2),
      };
    });
  };

  const computeReceiptForSave = useCallback(
    (source = {}) => computeReceiptWithFormattedTotals(source),
    [],
  );

  const summaryTotals = useMemo(
    () =>
      computeReceiptTotals({
        services: receipt.services || [],
        tires: receipt.tires || [],
        oilPrice: receipt.oilPrice,
        extraOilPrice: receipt.extraOilPrice,
        oilFilterPrice: receipt.oilFilterPrice,
        fullServiceIncluded: receipt.fullServiceIncluded,
        fullServicePrice: receipt.fullServicePrice,
        taxIncluded: receipt.taxIncluded,
        taxPct: receipt.taxType,
        discountIncluded: receipt.discountIncluded,
        discountType: receipt.discountType,
        discount: receipt.discount,
        paid: receipt.paid,
      }),
    [
      receipt.services,
      receipt.tires,
      receipt.oilPrice,
      receipt.extraOilPrice,
      receipt.oilFilterPrice,
      receipt.fullServiceIncluded,
      receipt.fullServicePrice,
      receipt.taxIncluded,
      receipt.taxType,
      receipt.discountIncluded,
      receipt.discountType,
      receipt.discount,
      receipt.paid,
    ],
  );

  const searchCustomers = (inputValue = "") => {
    const query = String(inputValue || "").trim();
    if (customerSearchTimeoutRef.current)
      clearTimeout(customerSearchTimeoutRef.current);
    if (!query) {
      customerSearchRequestIdRef.current += 1;
      setCustomerSearchLoading(false);
      setCustomersOptions([]);
      return;
    }
    customerSearchTimeoutRef.current = setTimeout(() => {
      const requestId = ++customerSearchRequestIdRef.current;
      setCustomerSearchLoading(true);
      axios
        .get(`${Helpers.baseUrl}/customers/search`, {
          ...Helpers.headers,
          params: { q: query },
        })
        .then((response) => {
          if (requestId !== customerSearchRequestIdRef.current) return;
          const data = Array.isArray(response.data) ? response.data : [];
          setCustomers(data);
          setCustomersOptions(data.map(buildCustomerOption));
        })
        .catch((error) => Helpers.unauthenticated(error, navigate))
        .finally(() => {
          if (requestId === customerSearchRequestIdRef.current)
            setCustomerSearchLoading(false);
        });
    }, 250);
  };

  const restoreVehicleOptions = (customerId = "") => {
    const selectedVehicleRecord = selectedVehicle?.value || null;
    if (customerId) {
      const mergedVehicles = [...(Array.isArray(vehicles) ? vehicles : [])];
      const selectedVehicleId = String(
        selectedVehicleRecord?._id || selectedVehicleRecord?.id || "",
      );
      if (
        selectedVehicleRecord &&
        selectedVehicleId &&
        !mergedVehicles.some(
          (vehicleItem) =>
            String(vehicleItem?._id || vehicleItem?.id || "") ===
            selectedVehicleId,
        )
      ) {
        mergedVehicles.push(selectedVehicleRecord);
      }
      setVehiclesOptions(buildVehicleOptions(mergedVehicles));
      return;
    }
    setVehiclesOptions(
      selectedVehicleRecord ? buildVehicleOptions([selectedVehicleRecord]) : [],
    );
  };

  const formatDateMMDDYYYY = (value) => {
    if (!value) return "";
    const parts = formatDateParts(value);
    if (parts) return `${parts.month}/${parts.day}/${parts.year}`;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const getCustomerDiscountPatch = (customer = {}) => {
    const discount = Number(customer?.discount) || 0;
    const discountType = customer?.discountType || "";
    const discountIncluded = Boolean(
      customer?.discountIncluded && discountType && discount > 0,
    );

    return {
      discountIncluded,
      discountType: discountIncluded ? discountType : "",
      discount: discountIncluded ? discount : 0,
    };
  };
  const hasServiceTechnician = (serviceOption = {}) => {
    const value = serviceOption?.value || serviceOption || {};
    if (value?.type === "part") return true;
    const techs = Array.isArray(value?.technicians)
      ? value.technicians
      : value?.technician
        ? [value.technician]
        : [];
    return techs.some(Boolean);
  };
  const hasTireTechnician = (tire = {}) => Boolean(tire?.technician);
  const getMissingTechnicianMessage = (source = {}) => {
    const services = Array.isArray(source.services) ? source.services : [];
    const tires = Array.isArray(source.tires) ? source.tires : [];
    const missingService = services.find(
      (service) => !hasServiceTechnician(service),
    );
    if (missingService) {
      const serviceName =
        missingService?.value?.name ||
        missingService?.name ||
        "selected service";
      return `Select technician for ${serviceName}`;
    }
    const missingTire = tires.find((tire) => !hasTireTechnician(tire));
    if (missingTire) {
      const tireName = [missingTire?.brand, missingTire?.size]
        .filter(Boolean)
        .join(" ");
      return `Select technician for ${tireName || "selected tire"}`;
    }
    return "";
  };
  const syncCustomerCollections = (nextCustomer, mode = "update") => {
    if (!nextCustomer?._id) return;
    setCustomers((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const existingIndex = list.findIndex(
        (item) => String(item?._id || "") === String(nextCustomer._id),
      );
      if (existingIndex >= 0) {
        return list.map((item, index) =>
          index === existingIndex ? nextCustomer : item,
        );
      }
      return mode === "add" ? [nextCustomer, ...list] : list;
    });
    setCustomersOptions((prev) => {
      const option = buildCustomerOption(nextCustomer);
      const list = Array.isArray(prev) ? prev : [];
      const existingIndex = list.findIndex(
        (item) => String(item?.value?._id || "") === String(nextCustomer._id),
      );
      if (existingIndex >= 0) {
        return list.map((item, index) =>
          index === existingIndex ? option : item,
        );
      }
      return mode === "add" ? [option, ...list] : list;
    });
  };
  const customerFields = useMemo(() => getCustomerFormFields(), []);
  const tireFields = useMemo(
    () => getTireFormFields(getVendorFieldOptions(tireForm?.vendor)),
    [getVendorFieldOptions, tireForm?.vendor],
  );
  const partFields = useMemo(
    () => getPartFormFields(getVendorFieldOptions(partForm?.vendor), partForm),
    [getVendorFieldOptions, partForm],
  );
  const buildQuoteOnlyPartOption = (values = {}) => {
    const payload = buildPartPayload(
      values,
      Helpers.authParentId,
      "Non Inventory",
    );
    const quoteOnlyPartId = `quote-part-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const part = {
      ...payload,
      _id: "",
      id: "",
      quoteOnlyPart: true,
      quoteOnlyPartId,
      partType: "Non Inventory",
      specialOrderStatus: "Quote Only",
      quantity: Math.max(1, Number(payload.quantity) || 1),
    };
    return {
      label: `${part.name}${part.partNumber ? ` (${part.partNumber})` : ""}`,
      value: part,
    };
  };
  const vehicleCustomerOptions = useMemo(
    () => buildVehicleCustomerOptions(customers),
    [customers],
  );
  const getCustomerId = (customer = null) => {
    if (!customer) return "";
    return typeof customer === "object"
      ? customer?._id || ""
      : String(customer || "");
  };
  const getVehicleCustomerId = (vehicle = null) => {
    if (!vehicle?.customer) return "";
    return getCustomerId(vehicle.customer);
  };
  const buildDraftTitle = (draftReceipt = receipt) => {
    const customerName = draftReceipt?.customer?.name || "New Estimate";
    const vehicleLabel = formatVehicleLabel(draftReceipt?.vehicle);
    return [customerName, vehicleLabel].filter(Boolean).join(" · ");
  };
  const hydrateDraftReceipt = (draftReceipt = {}) => {
    const customerOption = buildCustomerOption(draftReceipt?.customer);
    const vehicleOption = draftReceipt?.vehicle
      ? {
          label: formatVehicleLabel(draftReceipt.vehicle),
          value: draftReceipt.vehicle,
        }
      : "";
    const selectedServiceOptions = Array.isArray(draftReceipt?.services)
      ? draftReceipt.services.map((serviceItem) => {
          const value = serviceItem?.value ?? serviceItem ?? {};
          return { label: formatSelectedServiceLabel({ value }), value };
        })
      : [];
    const selectedTireDraftOptions = Array.isArray(draftReceipt?.tires)
      ? draftReceipt.tires.map((tireItem) => {
          const value = tireItem?.value ?? tireItem ?? {};
          return {
            label: `${value.condition || value.quality || "Used"} - ${value.brand || "Unknown"} ${value.size || ""} $${Number(value.price || 0)} Qty: ${Number(value.quantity || 0)}`,
            value,
          };
        })
      : [];
    const selectedOilOption = draftReceipt?.oil
      ? {
          label:
            `${draftReceipt.oil.brand || ""} ${draftReceipt.oil.name || ""}`.trim(),
          value: draftReceipt.oil,
        }
      : [];
    const selectedFilterOption = draftReceipt?.oilFilter
      ? {
          label:
            `${draftReceipt.oilFilter.brand || ""} ${draftReceipt.oilFilter.name || draftReceipt.oilFilter.type || ""}`.trim(),
          value: draftReceipt.oilFilter,
        }
      : null;
    const selectedExtraOilOption = draftReceipt?.extraOilSelection
      ? {
          label:
            `${draftReceipt.extraOilSelection.brand || ""} ${draftReceipt.extraOilSelection.name || ""}`.trim(),
          value: draftReceipt.extraOilSelection,
        }
      : null;
    const statusOpt =
      statusOptions.find((opt) => opt.value === draftReceipt?.status) || "";
    const paymentTypeOpt =
      paymentTypeOptions.find(
        (opt) => opt.value === draftReceipt?.paymentType,
      ) || "";
    const nextReceipt = {
      ...receiptInit,
      ...draftReceipt,
      date: draftReceipt?.date
        ? String(draftReceipt.date).slice(0, 10)
        : receiptInit.date,
    };
    const { services, tires, subtotal, tax, totalPrice, remaining } =
      computeReceiptTotals({
        services: selectedServiceOptions,
        tires: nextReceipt.tires || [],
        oilPrice: nextReceipt.oilPrice,
        extraOilPrice: nextReceipt.extraOilPrice,
        oilFilterPrice: nextReceipt.oilFilterPrice,
        fullServiceIncluded: nextReceipt.fullServiceIncluded,
        fullServicePrice: nextReceipt.fullServicePrice,
        taxIncluded: nextReceipt.taxIncluded,
        taxPct: nextReceipt.taxType,
        discountIncluded: nextReceipt.discountIncluded,
        discountType: nextReceipt.discountType,
        discount: nextReceipt.discount,
        paid: nextReceipt.paid,
      });
    setSelectedCustomer(customerOption);
    setSelectedVehicle(vehicleOption);
    setSelectedServices(selectedServiceOptions);
    setSelectedTireOptions(selectedTireDraftOptions);
    setSelectedOil(selectedOilOption);
    setSelectedOilFilter(selectedFilterOption);
    setSelectedExtraOil(selectedExtraOilOption);
    const savedExtraOilQty = Number(nextReceipt.extraOilQuantity) || 1;
    const savedExtraOilUnitPrice =
      Number(nextReceipt.extraOilUnitPrice) ||
      (savedExtraOilQty > 0
        ? (Number(nextReceipt.extraOilPrice) || 0) / savedExtraOilQty
        : 0);
    setExtraOilQty(savedExtraOilQty);
    setExtraOilUnitPrice(savedExtraOilUnitPrice);
    setOilTechnician(
      Array.isArray(nextReceipt.oilTechnician) ? nextReceipt.oilTechnician : [],
    );
    setselectedStatus(statusOpt);
    setSelectedPaymentType(paymentTypeOpt);
    setReceipt({
      ...nextReceipt,
      services,
      tires,
      subTotal: subtotal.toFixed(2),
      subtotal: subtotal.toFixed(2),
      tax: tax.toFixed(2),
      totalPrice: totalPrice.toFixed(2),
      remaining: remaining.toFixed(2),
    });
  };

  const filterCustomerOption = (option, rawInput) => {
    const input = String(rawInput || "").toLowerCase();
    if (!input) return true;
    const name = String(
      option?.value?.name || option?.label || "",
    ).toLowerCase();
    const email = String(option?.value?.email || "").toLowerCase();
    const phoneDigits = String(
      option?.value?.phone || option?.label || "",
    ).replace(/\D/g, "");
    const inputDigits = String(rawInput || "").replace(/\D/g, "");
    return (
      name.includes(input) ||
      email.includes(input) ||
      (inputDigits && phoneDigits.includes(inputDigits))
    );
  };

  const getPhoneFromCustomerSearch = () => {
    const digits = String(customerSearchInput || "")
      .replace(/\D/g, "")
      .slice(0, 10);
    return digits.length >= 7 ? formatPhone(digits) : "";
  };

  const handleCustomerInputChange = (inputValue, actionMeta) => {
    if (actionMeta?.action === "input-change") {
      setCustomerSearchInput(inputValue);
      searchCustomers(inputValue);
    }
    return inputValue;
  };

  const getTechnicians = () => {
    axios
      .get(`${Helpers.baseUrl}/teams/${Helpers.authParentId}`, Helpers.headers)
      .then((response) => {
        const sortedTechnicians = [...response.data].sort((a, b) =>
          String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
            sensitivity: "base",
          }),
        );
        setTechnicians(sortedTechnicians);
        setTechniciansOptions(
          sortedTechnicians.map((t) => ({ label: t.name, value: t })),
        );
      })
      .catch((error) => console.error("Failed to fetch technicians:", error));
  };

  const getServices = () => {
    axios
      .get(
        `${Helpers.baseUrl}/services/all/${Helpers.authParentId}`,
        Helpers.headers,
      )
      .then((response) => {
        const sortedServices = sortServiceRecords(
          Array.isArray(response.data) ? response.data : [],
        );
        setServices(sortedServices);
        const serviceArray = [
          createCustomServiceTemplateOption(),
          ...sortedServices.map(buildServiceOption),
        ];
        setServiceOptions(serviceArray);
      });
  };

  const getTires = () => {
    axios
      .get(
        `${Helpers.baseUrl}/tires/all/${Helpers.authParentId}`,
        Helpers.headers,
      )
      .then((response) => {
        const data = Array.isArray(response.data)
          ? response.data.reverse()
          : [];
        setTires(data);
        setRawTires(data);
      })
      .catch((error) => Helpers.unauthenticated(error, navigate));
  };

  const getParts = () => {
    const requestId = ++partSearchRequestIdRef.current;
    setPartSearchLoading(true);
    axios
      .get(
        `${Helpers.baseUrl}/parts/all/${Helpers.authParentId}`,
        Helpers.headers,
      )
      .then((response) => {
        const data = Array.isArray(response.data)
          ? response.data
          : [];
        setNonInventoryPartsOptions(
          sortPartsByName(
            data.filter(
              (part) =>
                part.partType === "Non Inventory" &&
                ["Available", "Assigned"].includes(
                  part.specialOrderStatus || "Available",
                ),
            ),
          ).map(buildPartCatalogOption),
        );
        if (requestId !== partSearchRequestIdRef.current) return;
        setPartsOptions(
          sortPartsByName(data.filter(isAvailableInventoryPart)).map(
            buildPartCatalogOption,
          ),
        );
      })
      .catch((error) => Helpers.unauthenticated(error, navigate))
      .finally(() => {
        setNonInventoryPartsLoading(false);
        if (requestId === partSearchRequestIdRef.current)
          setPartSearchLoading(false);
      });
  };

  const tiresOptions = useMemo(() => {
    return (rawTires || []).map((t) => {
      const id = t._id ?? t.id;
      const condition = t.condition ?? t.quality ?? "Used";
      const brand = t.brand ?? "Unknown";
      const size = t.size ?? "";
      const price = Number(t.price ?? 0);
      const qty = Number(t.quantity ?? 0);
      return {
        label: `${condition} - ${brand} ${size} $${price} Qty: ${qty}`,
        value: { id, ...t },
      };
    });
  }, [rawTires]);

  const getOilPriority = (oil) => {
    const priority = Number(oil?.priority);
    return Number.isFinite(priority) && priority > 0
      ? priority
      : Number.MAX_SAFE_INTEGER;
  };
  const sortOilRecords = (oils = []) =>
    [...oils].sort(
      (a, b) =>
        getOilPriority(a) - getOilPriority(b) ||
        String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
          sensitivity: "base",
        }),
    );
  const getFilterPriority = (filter) => {
    const priority = Number(filter?.priority);
    return Number.isFinite(priority) && priority > 0
      ? priority
      : Number.MAX_SAFE_INTEGER;
  };
  const sortFilterRecords = (filters = []) =>
    [...filters].sort(
      (a, b) =>
        getFilterPriority(a) - getFilterPriority(b) ||
        String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
          sensitivity: "base",
        }),
    );

  const extraOilOptions = useMemo(() => {
    const oil = selectedOil?.value;
    if (!oil) return [];
    const pricePerQuart = Number(oil.pricePerQuartz || 0).toFixed(2);
    return [
      {
        label: `${oil.name} (${oil.brand}) - $${pricePerQuart}/qt`,
        value: oil,
      },
    ];
  }, [selectedOil]);

  const getOils = () => {
    axios
      .get(
        `${Helpers.baseUrl}/oils/all/${Helpers.authParentId}`,
        Helpers.headers,
      )
      .then((response) => {
        const sortedOils = sortOilRecords(
          Array.isArray(response.data) ? response.data : [],
        );
        setOils(sortedOils);
        setOilsOptions(
          sortedOils.map((o) => ({
            label: `${o.name} (${o.brand}) - $${Number(o.pricePerVehicle || 0).toFixed(2)}/car`,
            value: o,
          })),
        );
      })
      .catch((error) => Helpers.unauthenticated(error, navigate));
  };

  const getFilters = () => {
    axios
      .get(
        `${Helpers.baseUrl}/filters/all/${Helpers.authParentId}`,
        Helpers.headers,
      )
      .then((response) => {
        const list = sortFilterRecords(
          Array.isArray(response.data) ? response.data : [],
        );
        setFiltersOptions(
          list.map((f) => ({
            label: `${f.name}${f.type ? ` (${f.type})` : ""}${f.quantity ? ` - Qty: ${f.quantity}` : ""} - $${Number(f.sellPrice || 0).toFixed(2)}`,
            value: f,
          })),
        );
      })
      .catch((error) => Helpers.unauthenticated(error, navigate));
  };

  async function loadLastOdometer(vehicleId) {
    try {
      const r = await axios.get(
        `${Helpers.baseUrl}/vehicles/${vehicleId}/last-odometer`,
        Helpers.headers,
      );
      const lm = r.data?.currentMiles ?? null;
      setLastMiles(lm);
      setReceipt((prev) => ({ ...prev, miles: lm ?? "" }));
    } catch (e) {
      setLastMiles(null);
      setReceipt((prev) => ({ ...prev, miles: "" }));
    }
  }

  async function syncVehicleCustomer(vehicleRecord, customerRecord) {
    const vehicleId = vehicleRecord?._id || vehicleRecord?.id;
    const customerId = getCustomerId(customerRecord);
    if (!vehicleId || !customerId) return;
    if (String(getVehicleCustomerId(vehicleRecord)) === String(customerId))
      return;
    const payload = buildVehiclePayload(
      { ...vehicleRecord, _id: vehicleId, customer: customerRecord },
      customers,
      Helpers.authParentId,
    );
    await axios.post(
      `${Helpers.baseUrl}/vehicles/update`,
      { ...payload, _id: vehicleId },
      Helpers.headers,
    );
  }

  const handleSaveReceipt = async (e, mode = "estimate", editDetails) => {
    e.preventDefault();
    if (btnLoading) return;
    if (isEditing && !editDetails) {
      setPendingEditMode(mode);
      setEditSaveError("");
      setShowEditReview(true);
      return;
    }
    setBtnLoading(true);
    const addOrUpdate = isEditing ? "update" : "add";
    const collectPayment = mode === "invoice" && !editingInvoice;
    const receiptType = editingInvoice || mode === "invoice" ? "Invoice" : "Quote";
    const tzOffsetMinutes = new Date().getTimezoneOffset();
    const payloadDate = buildDateTimeForSave();
    const receiptForSave = computeReceiptForSave(receipt);
    const missingTechnicianMessage =
      getMissingTechnicianMessage(receiptForSave);
    if (missingTechnicianMessage) {
      setBtnLoading(false);
      setErrors((prev) => ({
        ...prev,
        technician: missingTechnicianMessage,
        tireTechnician: missingTechnicianMessage,
      }));
      Helpers.toast("error", missingTechnicianMessage);
      if (isEditing) setEditSaveError(missingTechnicianMessage);
      return;
    }
    const payload = {
      ...receiptForSave,
      techNotes: receiptForSave.techNotes || "",
      receiptType,
      date: isDateOnlyString(receiptForSave.date)
        ? receiptForSave.date
        : payloadDate.toISOString(),
      tzOffsetMinutes,
      ...(isEditing ? { ...editDetails, expectedUpdatedAt: incomingEditReceipt?.updatedAt } : {}),
    };
    try {
      const { data: savedReceipt } = await axios.post(
        `${Helpers.baseUrl}/receipts/${addOrUpdate}`,
        { ...payload, tzOffsetMinutes },
        Helpers.headers,
      );
      const persistedReceiptId =
        savedReceipt?._id || payload?._id || receiptForSave?._id || null;
      try {
        await syncVehicleCustomer(
          receiptForSave?.vehicle,
          receiptForSave?.customer,
        );
      } catch (vehicleCustomerSyncError) {
        console.warn(
          "Vehicle customer sync failed:",
          vehicleCustomerSyncError?.response?.data || vehicleCustomerSyncError,
        );
      }
      const vehicleId = receiptForSave?.vehicle?._id || receiptForSave?.vehicle;
      const miles = Number(receiptForSave?.miles);
      if (vehicleId && !isNaN(miles) && miles > 0) {
        try {
          await axios.patch(
            `${Helpers.baseUrl}/vehicles/update-odometer/${vehicleId}`,
            { miles, unit: "mi", receiptId: persistedReceiptId },
            Helpers.headers,
          );
        } catch (err) {
          console.warn("Odometer update failed:", err?.response?.data || err);
        }
      }
      if (collectPayment) {
        const due = Number(
          savedReceipt?.remaining ?? receiptForSave.remaining ?? 0,
        );
        const total = Number(
          savedReceipt?.totalPrice ?? receiptForSave.totalPrice ?? 0,
        );
        const previousPaid = Number(
          savedReceipt?.paid ?? receiptForSave.paid ?? 0,
        );
        const initialPayment = calculatePaymentAmounts({
          amountDue: due,
          amountPaid: due,
          previousPaid,
          totalPrice: total,
        });
        setPayment({
          receipt_id: persistedReceiptId,
          amount_due: due,
          amount_remaining: initialPayment.remaining.toFixed(2),
          amount_paid: initialPayment.amountPaid,
          payment_type: selectedPaymentType?.value || "",
          payment_date: todayStr,
          status: initialPayment.status,
          paid: initialPayment.paid,
          previous_paid: previousPaid,
          total_price: total,
        });
        setPaymentErrors([]);
      }
      if (draftId && !isEditing) {
        try {
          await axios.delete(
            `${Helpers.baseUrl}/receipts/drafts/${draftId}`,
            Helpers.headers,
          );
        } catch (draftDeleteError) {
          console.warn("Draft cleanup failed:", draftDeleteError);
        }
        setLoadedDraftSession(false);
      }
      setDraftReceiptId(persistedReceiptId);
      setShowPaymentModal(collectPayment);
      setShowEditReview(false);
      setReceipt(receiptInit);
      setIsEditing(false);
      Helpers.toast("success", "Receipt saved successfully");
      if (!collectPayment) {
        navigate(isEditing ? `/user/receipts/receipt-details/${persistedReceiptId}` : "/user/estimates");
      }
    } catch (error) {
      setErrors(Helpers.error_response(error));
      if (isEditing) {
        setEditSaveError(error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || "Could not save these changes. Please try again.");
      }
    } finally {
      setBtnLoading(false);
    }
  };

  const closeCustomerModal = () => {
    setIsCustomerModelVisible(false);
    setCustomerModalMode("add");
    setCustomer(customerInit);
  };
  const openAddCustomerModal = () => {
    const searchedPhone = getPhoneFromCustomerSearch();
    setCustomerModalMode("add");
    setCustomer({
      ...customerInit,
      phone: searchedPhone || customerInit.phone,
    });
    setIsCustomerModelVisible(true);
  };
  const openEditCustomerModal = () => {
    if (!selectedCustomer?.value) return;
    setCustomerModalMode("edit");
    setCustomer(selectedCustomer.value);
    setIsCustomerModelVisible(true);
  };
  const handleSaveCustomer = (values) => {
    const payload = buildCustomerPayload(values, Helpers.authParentId);
    setCustomerBtnLoading(true);
    axios
      .post(
        `${Helpers.baseUrl}/customers/${customerModalMode === "edit" ? "update" : "add"}`,
        customerModalMode === "edit"
          ? { ...payload, _id: customer?._id || values?._id }
          : payload,
        Helpers.headers,
      )
      .then((response) => {
        const savedCustomer =
          customerModalMode === "edit"
            ? {
                ...(selectedCustomer?.value || {}),
                ...payload,
                ...(response?.data && typeof response.data === "object"
                  ? response.data
                  : {}),
                _id: customer?._id || values?._id,
              }
            : response.data;
        const nextCustomerOption = buildCustomerOption(savedCustomer);
        syncCustomerCollections(savedCustomer, customerModalMode);
        handleSelectCustomer(nextCustomerOption);
        setCustomer(customerInit);
        Helpers.toast(
          "success",
          `Customer ${customerModalMode === "edit" ? "updated" : "saved"} successfully`,
        );
        closeCustomerModal();
        setCustomerBtnLoading(false);
      })
      .catch((error) => {
        Helpers.error_response(error);
        setCustomerBtnLoading(false);
      });
  };
  const closeAddTireModal = () => {
    setIsAddTireModalVisible(false);
    setTireForm(createTireInitialValues(Helpers.authParentId));
  };
  const handleSaveTire = (values) => {
    const payload = buildTirePayload(values, Helpers.authParentId);
    setTireBtnLoading(true);
    axios
      .post(`${Helpers.baseUrl}/tires/add`, payload, Helpers.headers)
      .then((response) => {
        const tire = response.data;
        const option = {
          label: `${tire.quality || "Used"} - ${tire.brand || "Unknown"} ${tire.size || ""} $${Number(tire.price || 0)} Qty: ${Number(tire.quantity || 0)}`,
          value: { id: tire._id ?? tire.id, ...tire },
        };
        setSelectedTireOptions((prev) => [
          ...(Array.isArray(prev) ? prev : []),
          option,
        ]);
        setPendingTire({
          ...option.value,
          id: option.value._id || option.value.id,
          price: Number(option.value.price) || 0,
          available: Number(option.value.quantity) || 0,
          quantity: "",
          technician: null,
          total_price: 0,
          type: option.value.type || "tire_service",
          tax: option.value.tax ?? "Taxable",
        });
        getTires();
        Helpers.toast("success", "Tire saved successfully");
        closeAddTireModal();
        setIsTireModalVisible(true);
      })
      .catch((error) => {
        Helpers.error_response(error);
      })
      .finally(() => {
        setTireBtnLoading(false);
      });
  };
  const openVehicleModal = () => {
    setVehicle(
      prepareVehicleFormValues(
        { customer: selectedCustomer?.value?._id || "" },
        Helpers.authParentId,
      ),
    );
    setOnlyFillEmptyVehicleVin(true);
    onlyFillEmptyRef.current = true;
    setIsVehicleModelVisible(true);
  };
  const handleSaveVehicle = async (values) => {
    setVehicleBtnLoading(true);
    try {
      const payload = buildVehiclePayload(
        values,
        customers,
        Helpers.authParentId,
      );
      const { data } = await axios.post(
        `${Helpers.baseUrl}/vehicles/add`,
        payload,
        Helpers.headers,
      );
      await handleSelectVehicle({
        label: formatVehicleLabel(data),
        value: data,
      });
      setVehicle(vehicleInit);
      Helpers.toast("success", "Vehicle saved successfully");
      closeVehicleModal();
    } catch (error) {
      Helpers.error_response(error);
    } finally {
      setVehicleBtnLoading(false);
    }
  };
  const closeVehicleModal = () => {
    setIsVehicleModelVisible(false);
    setVehicle(vehicleInit);
    setOnlyFillEmptyVehicleVin(true);
    onlyFillEmptyRef.current = true;
    lastDecodedVinRef.current = "";
    lastDecodedVehicleRef.current = null;
  };
  const handleSelectCustomer = (selectedCustomer) => {
    setCustomerSearchInput("");
    setVehicleSearchInput("");
    const currentVehicleOption =
      selectedVehicle && selectedVehicle.value ? selectedVehicle : null;
    const currentVehicle =
      currentVehicleOption?.value || receipt?.vehicle || null;
    const currentVehicleCustomerId = getVehicleCustomerId(currentVehicle);

    if (!selectedCustomer || !selectedCustomer.value) {
      const keepVehicle = currentVehicle && !currentVehicleCustomerId;
      setSelectedCustomer(null);
      if (keepVehicle) {
        setVehiclesOptions(buildVehicleOptions([currentVehicle]));
      } else {
        setSelectedVehicle("");
        setVehiclesOptions([]);
      }
      setVehicle((prev) => ({ ...prev, customer: "" }));
      setReceipt((prev) => {
        const updatedReceipt = {
          ...prev,
          customer: null,
          vehicle: keepVehicle ? currentVehicle : "",
          taxIncluded: false,
          taxType: 0,
          discountIncluded: false,
          discountType: "",
          discount: 0,
        };
        const whichServices =
          (selectedServices?.length
            ? selectedServices
            : updatedReceipt.services) || [];
        const { services, tires, subtotal, tax, totalPrice, remaining } =
          computeReceiptTotals({
            services: whichServices,
            tires: updatedReceipt.tires || [],
            oilPrice: updatedReceipt.oilPrice,
            extraOilPrice: updatedReceipt.extraOilPrice,
            oilFilterPrice: updatedReceipt.oilFilterPrice,
            fullServiceIncluded: updatedReceipt.fullServiceIncluded,
            fullServicePrice: updatedReceipt.fullServicePrice,
            taxIncluded: updatedReceipt.taxIncluded,
            taxPct: updatedReceipt.taxType,
            discountIncluded: updatedReceipt.discountIncluded,
            discountType: updatedReceipt.discountType,
            discount: updatedReceipt.discount,
            paid: updatedReceipt.paid,
          });
        return {
          ...updatedReceipt,
          services,
          tires,
          subTotal: subtotal.toFixed(2),
          subtotal: subtotal.toFixed(2),
          tax: tax.toFixed(2),
          totalPrice: totalPrice.toFixed(2),
          remaining: remaining.toFixed(2),
        };
      });
      return;
    }
    const nextCustomer = selectedCustomer.value;
    syncCustomerCollections(nextCustomer, "add");
    const nextCustomerId = getCustomerId(nextCustomer);
    const keepVehicle =
      currentVehicle &&
      (!currentVehicleCustomerId ||
        String(currentVehicleCustomerId) === String(nextCustomerId));
    setSelectedCustomer(selectedCustomer);
    if (!keepVehicle) setSelectedVehicle("");
    getCustomerVehicles(nextCustomerId).then((data) => {
      if (!keepVehicle || !currentVehicle) return;
      const nextVehicles = [...(Array.isArray(data) ? data : [])];
      const currentVehicleId = String(
        currentVehicle?._id || currentVehicle?.id || "",
      );
      if (
        currentVehicleId &&
        !nextVehicles.some(
          (vehicleItem) =>
            String(vehicleItem?._id || vehicleItem?.id || "") ===
            currentVehicleId,
        )
      ) {
        nextVehicles.push(currentVehicle);
      }
      setVehiclesOptions(buildVehicleOptions(nextVehicles));
    });
    setVehicle((prev) => ({ ...prev, customer: nextCustomerId || "" }));
    setReceipt((prev) => {
      const updatedReceipt = {
        ...prev,
        customer: nextCustomer,
        vehicle: keepVehicle ? prev.vehicle || currentVehicle : "",
        taxIncluded: nextCustomer.tax === "Taxable",
        taxType: nextCustomer.tax === "Taxable" ? resolvedTaxRate : 0,
        ...getCustomerDiscountPatch(nextCustomer),
      };
      const whichServices =
        (selectedServices?.length
          ? selectedServices
          : updatedReceipt.services) || [];
      const { services, tires, subtotal, tax, totalPrice, remaining } =
        computeReceiptTotals({
          services: whichServices,
          tires: updatedReceipt.tires || [],
          oilPrice: updatedReceipt.oilPrice,
          extraOilPrice: updatedReceipt.extraOilPrice,
          oilFilterPrice: updatedReceipt.oilFilterPrice,
          fullServiceIncluded: updatedReceipt.fullServiceIncluded,
          fullServicePrice: updatedReceipt.fullServicePrice,
          taxIncluded: updatedReceipt.taxIncluded,
          taxPct: updatedReceipt.taxType,
          discountIncluded: updatedReceipt.discountIncluded,
          discountType: updatedReceipt.discountType,
          discount: updatedReceipt.discount,
          paid: updatedReceipt.paid,
        });
      return {
        ...updatedReceipt,
        services,
        tires,
        subTotal: subtotal.toFixed(2),
        subtotal: subtotal.toFixed(2),
        tax: tax.toFixed(2),
        totalPrice: totalPrice.toFixed(2),
        remaining: remaining.toFixed(2),
      };
    });
  };
  const handleSelectVehicle = async (selectedVehicle) => {
    setVehicleSearchInput("");
    setSelectedVehicle(selectedVehicle);
    if (!selectedVehicle) {
      restoreVehicleOptions(selectedCustomer?.value?._id || "");
      setReceipt((prev) => ({ ...prev, vehicle: null, miles: "" }));
      setLastMiles(null);
      return;
    }
    const vehicleData = selectedVehicle.value;
    const vehicleCustomer =
      vehicleData?.customer && typeof vehicleData.customer === "object"
        ? vehicleData.customer
        : null;
    const customerId = vehicleCustomer?._id || vehicleData?.customer || "";
    if (vehicleCustomer) {
      setSelectedCustomer(buildCustomerOption(vehicleCustomer));
      setVehicle((prev) => ({ ...prev, customer: vehicleCustomer._id || "" }));
    } else if (selectedCustomer?.value?._id) {
      setVehicle((prev) => ({
        ...prev,
        customer: selectedCustomer.value._id || "",
      }));
    }
    setReceipt((prev) => {
      const updatedReceipt = {
        ...prev,
        customer: vehicleCustomer || prev.customer,
        vehicle: vehicleData,
        taxIncluded: vehicleCustomer
          ? vehicleCustomer.tax === "Taxable"
          : prev.taxIncluded,
        taxType: vehicleCustomer
          ? vehicleCustomer.tax === "Taxable"
            ? resolvedTaxRate
            : 0
          : prev.taxType,
        ...(vehicleCustomer ? getCustomerDiscountPatch(vehicleCustomer) : {}),
      };
      const { services, tires, subtotal, tax, totalPrice, remaining } =
        computeReceiptTotals({
          services: updatedReceipt.services || [],
          tires: updatedReceipt.tires || [],
          oilPrice: updatedReceipt.oilPrice,
          extraOilPrice: updatedReceipt.extraOilPrice,
          oilFilterPrice: updatedReceipt.oilFilterPrice,
          fullServiceIncluded: updatedReceipt.fullServiceIncluded,
          fullServicePrice: updatedReceipt.fullServicePrice,
          taxIncluded: updatedReceipt.taxIncluded,
          taxPct: updatedReceipt.taxType,
          discountIncluded: updatedReceipt.discountIncluded,
          discountType: updatedReceipt.discountType,
          discount: updatedReceipt.discount,
          paid: updatedReceipt.paid,
        });
      return {
        ...updatedReceipt,
        services,
        tires,
        subTotal: subtotal.toFixed(2),
        subtotal: subtotal.toFixed(2),
        tax: tax.toFixed(2),
        totalPrice: totalPrice.toFixed(2),
        remaining: remaining.toFixed(2),
      };
    });
    if (customerId) getCustomerVehicles(customerId);
    try {
      const res = await axios.get(
        `${Helpers.baseUrl}/vehicles/${vehicleData._id || vehicleData}/last-odometer`,
        Helpers.headers,
      );
      if (res.data?.ok) {
        const currentMiles = res.data.currentMiles || 0;
        setLastMiles(currentMiles);
        setReceipt((prev) => ({ ...prev, miles: currentMiles }));
      } else {
        setLastMiles(null);
        setReceipt((prev) => ({ ...prev, miles: "" }));
      }
    } catch (err) {
      console.warn("Could not fetch last odometer:", err);
      setLastMiles(null);
      setReceipt((prev) => ({ ...prev, miles: "" }));
    }
  };
  const getCustomerVehicles = useCallback((customerId) => {
    if (!customerId) {
      setVehicles([]);
      setVehiclesOptions([]);
      return Promise.resolve([]);
    }
    return axios
      .get(
        `${Helpers.baseUrl}/vehicles/customer/${customerId}`,
        Helpers.headers,
      )
      .then((response) => {
        const data = Array.isArray(response.data)
          ? response.data.reverse()
          : [];
        setVehicles(data);
        setVehiclesOptions(buildVehicleOptions(data));
        return data;
      })
      .catch((error) => Helpers.unauthenticated(error, navigate));
  }, [navigate]);
  const searchVehicles = (inputValue = "") => {
    const query = String(inputValue || "").trim();
    setVehicleSearchInput(query);
    if (vehicleSearchTimeoutRef.current)
      clearTimeout(vehicleSearchTimeoutRef.current);
    if (!query) {
      vehicleSearchRequestIdRef.current += 1;
      setVehicleSearchLoading(false);
      restoreVehicleOptions(selectedCustomer?.value?._id || "");
      return;
    }
    vehicleSearchTimeoutRef.current = setTimeout(() => {
      const requestId = ++vehicleSearchRequestIdRef.current;
      setVehicleSearchLoading(true);
      axios
        .get(`${Helpers.baseUrl}/vehicles/search`, {
          ...Helpers.headers,
          params: { q: query },
        })
        .then((response) => {
          if (requestId !== vehicleSearchRequestIdRef.current) return;
          setVehiclesOptions(buildVehicleOptions(response.data));
        })
        .catch((error) => Helpers.unauthenticated(error, navigate))
        .finally(() => {
          if (requestId !== vehicleSearchRequestIdRef.current) return;
          setVehicleSearchLoading(false);
        });
    }, 250);
  };
  const handleVehicleInputChange = (inputValue, actionMeta) => {
    if (actionMeta?.action === "input-change") searchVehicles(inputValue);
    return inputValue;
  };
  const getReceiptPartFields = (part = {}, price = 0) => ({
    ...(part._id ? { _id: part._id } : {}),
    ...(part.id ? { id: part.id } : {}),
    ...(part.quoteOnlyPart
      ? { quoteOnlyPart: true, quoteOnlyPartId: part.quoteOnlyPartId || "" }
      : {}),
    partNumber: part.partNumber || "",
    partType: part.partType || "Inventory",
    vendor: part.vendor || "",
    buyPrice: Number(part.buyPrice || 0),
    sellPrice: Number(part.sellPrice || price || 0),
    invoiceNo: part.invoiceNo || "",
    invoiceDate: part.invoiceDate || null,
    specialOrderStatus: part.specialOrderStatus || "",
    refundAmount: Number(part.refundAmount || 0),
    statusNotes: part.statusNotes || "",
  });

  const handleSelectOil = (selectedOil) => {
    if (selectedOil == null) {
      setSelectedOil("");
      setSelectedExtraOil(null);
      setExtraOilQty(1);
      setExtraOilUnitPrice(0);
      updateReceiptTotals({
        oil: "",
        oilPrice: 0,
        oilQuantity: 0,
        extraOilSelection: null,
        extraOilPrice: 0,
        extraOilQuantity: 0,
        fullServiceIncluded: false,
        fullServicePrice: 0,
      });
    } else {
      setSelectedOil(selectedOil);
      setSelectedExtraOil(null);
      setExtraOilQty(1);
      setExtraOilUnitPrice(0);
      const pricePerVehicle = parseFloat(selectedOil.value.pricePerVehicle);
      updateReceiptTotals({
        oil: selectedOil.value,
        oilPrice: pricePerVehicle,
        oilQuantity: 5,
        extraOilSelection: null,
        extraOilPrice: 0,
        extraOilQuantity: 0,
      });
    }
  };

  const handleAddOilChangeRow = () => {
    updateReceiptTotals({ oilChangeAdded: true });
    return true;
  };
  const handleRemoveOilChangeRow = () => {
    updateReceiptTotals({
      oilChangeAdded: false,
      oil: "",
      oilPrice: 0,
      oilQuantity: 0,
      extraOilSelection: null,
      extraOilPrice: 0,
      extraOilQuantity: 0,
      extraOilUnitPrice: 0,
      oilFilter: "",
      oilFilterPrice: 0,
      oilFilterQuantity: 1,
      fullServiceIncluded: false,
      fullServicePrice: 0,
      oilTechnician: [],
    });
    setSelectedOil("");
    setSelectedOilFilter(null);
    setSelectedExtraOil(null);
    setExtraOilQty(1);
    setExtraOilUnitPrice(0);
    setOilTechnician([]);
  };

  const handleOilPriceOverride = (val) => {
    updateReceiptTotals({ oilPrice: val === "" ? 0 : Number(val) || 0 });
  };
  const handleToggleFullService = (checked) => {
    updateReceiptTotals({
      fullServiceIncluded: checked,
      fullServicePrice: checked ? FULL_SERVICE_PRICE : 0,
    });
  };
  const handleSelectExtraOil = (opt) => {
    if (!opt) {
      setSelectedExtraOil(null);
      setExtraOilQty(1);
      setExtraOilUnitPrice(0);
      updateReceiptTotals({
        extraOilSelection: null,
        extraOilQuantity: 0,
        extraOilPrice: 0,
      });
      return;
    }
    const unit = Number(opt.value?.pricePerQuartz || 0);
    setSelectedExtraOil(opt);
    setExtraOilQty(1);
    setExtraOilUnitPrice(unit);
    updateReceiptTotals({
      extraOilSelection: opt.value,
      extraOilUnitPrice: unit,
      extraOilQuantity: 1,
      extraOilPrice: unit,
    });
  };
  const handleExtraOilQtyChange = (val) => {
    const qty = Math.max(0, Number(val) || 0);
    setExtraOilQty(qty);
    updateReceiptTotals({
      extraOilQuantity: qty,
      extraOilPrice: (qty || 0) * (extraOilUnitPrice || 0),
    });
  };
  const handleExtraOilUnitPriceChange = (val) => {
    const unit = Math.max(0, Number(val) || 0);
    setExtraOilUnitPrice(unit);
    updateReceiptTotals({
      extraOilUnitPrice: unit,
      extraOilPrice: (Number(extraOilQty) || 0) * unit,
    });
  };
  const handleSelectOilTechnician = (techList) => {
    const list = Array.isArray(techList)
      ? techList
      : techList
        ? [techList]
        : [];
    setOilTechnician(list);
    updateReceiptTotals({ oilTechnician: list });
  };
  const handleSelectOilFilter = (selectedFilter) => {
    if (!selectedFilter) {
      setSelectedOilFilter(null);
      updateReceiptTotals({
        oilFilter: "",
        oilFilterPrice: 0,
        oilFilterQuantity: 1,
      });
      return;
    }
    const price = Number(selectedFilter.value?.sellPrice || 0);
    setSelectedOilFilter(selectedFilter);
    updateReceiptTotals({
      oilFilter: selectedFilter.value,
      oilFilterPrice: price,
      oilFilterQuantity: 1,
    });
  };
  const handleOilFilterPriceOverride = (val) => {
    updateReceiptTotals({ oilFilterPrice: val === "" ? 0 : Number(val) || 0 });
  };
  const handleDiscount = (e) => {
    const raw = e.target.value;
    const parsed = raw === "" ? "" : Number.parseFloat(raw) || 0;
    updateReceiptTotals({
      discountIncluded: true,
      discount: parsed,
    });
  };
  const handleDiscountTypeChange = (e) => {
    const discountType = e.target.value;
    updateReceiptTotals({
      discountIncluded: Boolean(discountType),
      discountType,
      discount: discountType ? receipt.discount || 0 : 0,
    });
  };
  const clearDecodedVehicleFieldsIfNeeded = useCallback((nextVin) => {
    const normalizedVin = String(nextVin || "")
      .trim()
      .toUpperCase();
    const lastDecodedVin = String(lastDecodedVinRef.current || "")
      .trim()
      .toUpperCase();
    if (!lastDecodedVin || normalizedVin === lastDecodedVin) return;
    const setFieldValue = vehicleFormikSetFieldValueRef.current;
    const values = vehicleFormikValuesRef.current || {};
    const lastDecodedVehicle = lastDecodedVehicleRef.current;
    if (!setFieldValue || !lastDecodedVehicle) {
      lastDecodedVinRef.current = "";
      lastDecodedVehicleRef.current = null;
      return;
    }
    [
      "year",
      "make",
      "model",
      "engine_size",
      "engine_cylinders",
      "drivetrain",
    ].forEach((field) => {
      if ((values?.[field] || "") === (lastDecodedVehicle?.[field] || ""))
        setFieldValue(field, "");
    });
    lastDecodedVinRef.current = "";
    lastDecodedVehicleRef.current = null;
  }, []);

  const handleVinDecoded = (mapped, onlyFillEmpty, sourceVin = "") => {
    const setFieldValue = vehicleFormikSetFieldValueRef.current;
    const values = vehicleFormikValuesRef.current || {};
    if (!setFieldValue) return;
    const normalizedDecodedVehicle = mapVinDecodedVehicle(mapped, {}, false);
    const nextValues = mapVinDecodedVehicle(mapped, values, onlyFillEmpty);
    setFieldValue("year", nextValues.year);
    setFieldValue("make", nextValues.make);
    setFieldValue("model", nextValues.model);
    setFieldValue("engine_size", nextValues.engine_size);
    setFieldValue("engine_cylinders", nextValues.engine_cylinders);
    setFieldValue("drivetrain", nextValues.drivetrain);
    lastDecodedVinRef.current = String(sourceVin || "")
      .trim()
      .toUpperCase();
    lastDecodedVehicleRef.current = normalizedDecodedVehicle;
  };

  const { vinLoading, vinError, onlyFillEmptyRef, onVinChange, onVinBlur } =
    useVinDecoder(handleVinDecoded, {
      baseUrl: Helpers.baseUrl,
      axiosConfig: Helpers.headers,
      endpointPrefix: "vin/",
    });

  const vehicleFields = useMemo(
    () =>
      getVehicleFormFields({
        customerOptions: vehicleCustomerOptions,
        customersLoaded: vehicleCustomerOptions.length > 0,
        onlyFillEmptyVin: onlyFillEmptyVehicleVin,
        setOnlyFillEmptyVin: setOnlyFillEmptyVehicleVin,
        onlyFillEmptyRef,
        onVinInputChange: clearDecodedVehicleFieldsIfNeeded,
        onVinChange,
        onVinBlur,
        vinLoading,
        vinError,
        formikValuesRef: vehicleFormikValuesRef,
        formikSetFieldValueRef: vehicleFormikSetFieldValueRef,
      }),
    [
      vehicleCustomerOptions,
      onlyFillEmptyVehicleVin,
      onlyFillEmptyRef,
      clearDecodedVehicleFieldsIfNeeded,
      onVinChange,
      onVinBlur,
      vinLoading,
      vinError,
    ],
  );

  const updatePaymentRemaining = (paidValue) => {
    const nextPayment = calculatePaymentAmounts({
      amountDue: payment.amount_due,
      amountPaid: paidValue,
      previousPaid: payment.previous_paid,
      totalPrice: payment.total_price,
    });
    setPayment((prev) => ({
      ...prev,
      amount_paid: paidValue === "" ? "" : nextPayment.amountPaid,
      amount_remaining: nextPayment.remaining.toFixed(2),
      status: nextPayment.status,
      paid: nextPayment.paid,
    }));
  };
  const copyDueToPaid = () => updatePaymentRemaining(payment.amount_due || 0);
  const handleSavePaymentInline = (printAfterSave = false) => {
    if (paymentSaving) return;
    const amountPaid = Number(payment.amount_paid);
    if (payment.amount_paid === "" || !Number.isFinite(amountPaid) || amountPaid < 0) {
      setPaymentErrors({ amount_paid: "Enter an amount of zero or greater" });
      return;
    }
    const payload = {
      ...payment,
      receipt_id: draftReceiptId,
      payment_type: payment.payment_type || selectedPaymentType?.value || "",
      payment_date: payment.payment_date || todayStr,
    };
    const finishInvoice = () => {
      setShowPaymentModal(false);
      if (printAfterSave) {
        navigate(
          `/user/receipts/receipt-details/${payload.receipt_id}?autoprint=1`,
        );
      } else {
        navigate("/user/receipts");
      }
    };
    setPaymentErrors({});
    // The invoice is already persisted. Zero paid needs no payment record.
    if (amountPaid === 0) {
      finishInvoice();
      return;
    }
    if (!payload.payment_type) {
      setPaymentErrors({ payment_type: "Please select a payment type" });
      return;
    }
    setPaymentSaving(true);
    axios
      .post(`${Helpers.baseUrl}/receipts/add-payment`, payload, Helpers.headers)
      .then(() => {
        finishInvoice();
        if (!printAfterSave)
          Helpers.toast("success", "Payment added successfully");
      })
      .catch((error) => {
        setPaymentErrors({
          ...Helpers.error_response(error),
          ...(error.response?.data?.amount_paid
            ? { amount_paid: error.response.data.amount_paid }
            : {}),
        });
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => setPaymentSaving(false));
  };
  const formatDateInput = (value) => {
    if (!value) return "";
    if (isDateOnlyString(value)) return value;
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : toLocalDateInput(d);
  };
  const buildDateTimeForSave = () => {
    if (!receipt.date) return new Date();
    if (typeof receipt.date === "string" && receipt.date.includes("T")) {
      const d = new Date(receipt.date);
      if (!isNaN(d.getTime())) return d;
    }
    const timePart = new Date().toTimeString().slice(0, 8);
    return new Date(`${receipt.date}T${timePart}`);
  };
  const getTax = () => {
    axios
      .get(
        `${Helpers.baseUrl}/customers/get-tax/${Helpers.authParentId}`,
        Helpers.headers,
      )
      .then((response) => setTaxValue(response.data))
      .catch((error) => Helpers.unauthenticated(error, navigate));
  };
  const searchParts = (inputValue = "") => {
    const query = String(inputValue || "").trim();
    setPartSearchInput(query);
    if (partSearchTimeoutRef.current)
      clearTimeout(partSearchTimeoutRef.current);
    if (!query) {
      getParts();
      return;
    }
    partSearchTimeoutRef.current = setTimeout(() => {
      const requestId = ++partSearchRequestIdRef.current;
      setPartSearchLoading(true);
      axios
        .get(`${Helpers.baseUrl}/parts/search`, {
          ...Helpers.headers,
          params: { q: query },
        })
        .then((response) => {
          if (requestId !== partSearchRequestIdRef.current) return;
          const data = Array.isArray(response.data) ? response.data : [];
          setPartsOptions(
            sortPartsByName(data.filter(isAvailableInventoryPart)).map(
              buildPartCatalogOption,
            ),
          );
        })
        .catch((error) => Helpers.unauthenticated(error, navigate))
        .finally(() => {
          if (requestId === partSearchRequestIdRef.current)
            setPartSearchLoading(false);
        });
    }, 250);
  };
  const handleSavePartInline = (values) => {
    const option = buildQuoteOnlyPartOption(values);
    const part = option.value;
    setSelectedPart(option);
    setPartDescription(part.description || "");
    setPartUnitPrice(Number(part.sellPrice || 0));
    setPartQty(Math.max(1, Number(part.quantity) || 1));
    setIsAddPartModalVisible(false);
    setPartForm(createPartInitialValues(Helpers.authParentId, "Non Inventory"));
    Helpers.toast("success", "Part added to receipt");
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    getServices();
    getTax();
    getTires();
    getParts();
    getTechnicians();
    getOils();
    getFilters();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (vehicleSearchTimeoutRef.current)
        clearTimeout(vehicleSearchTimeoutRef.current);
      if (customerSearchTimeoutRef.current)
        clearTimeout(customerSearchTimeoutRef.current);
      if (partSearchTimeoutRef.current)
        clearTimeout(partSearchTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setLoadedDraftSession(false);
    draftBootstrapStartedRef.current = false;
  }, [draftId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      draftId ||
      incomingEditReceipt ||
      draftBootstrapStartedRef.current
    )
      return;
    draftBootstrapStartedRef.current = true;
    axios
      .post(
        `${Helpers.baseUrl}/receipts/drafts/create`,
        {
          payload: { receipt: receiptInit },
          title: "New Estimate",
          receiptType: "Quote",
        },
        Helpers.headers,
      )
      .then((response) => {
        if (!response?.data?._id) {
          draftBootstrapStartedRef.current = false;
          return;
        }
        navigate(`/user/receipts/add/${response.data._id}`, {
          replace: true,
          state: location.state,
        });
      })
      .catch((error) => {
        draftBootstrapStartedRef.current = false;
        if (error?.response?.status === 404) {
          Helpers.toast(
            "error",
            "Draft routes are unavailable. Restart the backend and try again.",
          );
          return;
        }
        Helpers.unauthenticated(error, navigate);
        if (
          error?.response?.data?.error &&
          error?.response?.data?.error !== "Unauthenticated"
        ) {
          Helpers.toast("error", "Could not create draft session");
        }
      });
  }, [draftId, incomingEditReceipt, location.state, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!draftId || incomingEditReceipt) return;
    let cancelled = false;
    axios
      .get(`${Helpers.baseUrl}/receipts/drafts/${draftId}`, Helpers.headers)
      .then((response) => {
        if (cancelled) return;
        const draftReceipt = response?.data?.payload?.receipt;
        if (draftReceipt) hydrateDraftReceipt(draftReceipt);
        setLoadedDraftSession(true);
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          Helpers.toast("error", "Draft not found");
          navigate("/user/estimates");
          return;
        }
        Helpers.unauthenticated(error, navigate);
      });
    return () => {
      cancelled = true;
    };
  }, [draftId, incomingEditReceipt, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const incoming = incomingEditReceipt;
    if (!incoming || loadedEditPrefill) return;
    hydrateFromReceipt({
      data: incoming,
      serviceOptions,
      rawTires,
      oilsOptions,
      statusOptions,
      paymentTypeOptions,
      computeTotals: (args = {}) =>
        computeReceiptTotals({
          ...args,
          services: ensureServiceLineItemIds(args.services),
        }),
      setSelectedCustomer,
      setSelectedVehicle,
      setSelectedServices: (services) =>
        setSelectedServices(ensureServiceLineItemIds(services)),
      setSelectedTireOptions,
      setSelectedOil,
      setselectedStatus,
      setSelectedPaymentType,
      setReceipt,
      setIsEditing,
      setLoadedEditPrefill,
    });
  }, [
    incomingEditReceipt,
    serviceOptions,
    rawTires,
    oilsOptions,
    statusOptions,
    paymentTypeOptions,
    loadedEditPrefill,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!draftId || !loadedDraftSession || isEditing) return undefined;
    if (draftSaveTimeoutRef.current) clearTimeout(draftSaveTimeoutRef.current);
    draftSaveTimeoutRef.current = setTimeout(() => {
      axios
        .post(
          `${Helpers.baseUrl}/receipts/drafts/${draftId}/save`,
          {
            payload: { receipt },
            title: buildDraftTitle(receipt),
            receiptType: receipt?.receiptType || "Quote",
          },
          Helpers.headers,
        )
        .catch((error) => {
          console.warn(
            "Draft autosave failed:",
            error?.response?.data || error,
          );
        });
    }, 800);
    return () => {
      if (draftSaveTimeoutRef.current)
        clearTimeout(draftSaveTimeoutRef.current);
    };
  }, [draftId, loadedDraftSession, isEditing, receipt]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const prefillVehicle = location.state?.prefillVehicle;
    if (
      !prefillVehicle ||
      !draftId ||
      !loadedDraftSession ||
      loadedVehiclePrefill ||
      incomingEditReceipt
    )
      return;
    const prefillCustomer =
      location.state?.prefillCustomer || prefillVehicle.customer || null;
    if (prefillCustomer?._id) {
      setSelectedCustomer(buildCustomerOption(prefillCustomer));
      getCustomerVehicles(prefillCustomer._id);
    }
    setSelectedVehicle({
      label: formatVehicleLabel(prefillVehicle),
      value: prefillVehicle,
    });
    setReceipt((prev) => ({
      ...prev,
      customer: prefillCustomer || prev.customer,
      vehicle: prefillVehicle,
      taxIncluded: prefillCustomer?.tax === "Taxable",
      taxType:
        prefillCustomer?.tax === "Taxable" ? resolvedTaxRate : prev.taxType,
    }));
    if (prefillVehicle?._id) loadLastOdometer(prefillVehicle._id);
    setLoadedVehiclePrefill(true);
  }, [
    draftId,
    loadedDraftSession,
    location.state,
    loadedVehiclePrefill,
    loadedEditPrefill,
    incomingEditReceipt,
    resolvedTaxRate,
    getCustomerVehicles,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      !receipt.taxIncluded ||
      resolvedTaxRate <= 0 ||
      Number(receipt.taxType) > 0
    )
      return;
    setReceipt((prev) => {
      if (!prev.taxIncluded || resolvedTaxRate <= 0 || Number(prev.taxType) > 0)
        return prev;
      const { services, tires, subtotal, tax, totalPrice, remaining } =
        computeReceiptTotals({
          services: prev.services || [],
          tires: prev.tires || [],
          oilPrice: prev.oilPrice,
          extraOilPrice: prev.extraOilPrice,
          oilFilterPrice: prev.oilFilterPrice,
          fullServiceIncluded: prev.fullServiceIncluded,
          fullServicePrice: prev.fullServicePrice,
          taxIncluded: true,
          taxPct: resolvedTaxRate,
          discountIncluded: prev.discountIncluded,
          discountType: prev.discountType,
          discount: prev.discount,
          paid: prev.paid,
        });
      return {
        ...prev,
        services,
        tires,
        taxType: resolvedTaxRate,
        subTotal: subtotal.toFixed(2),
        subtotal: subtotal.toFixed(2),
        tax: tax.toFixed(2),
        totalPrice: totalPrice.toFixed(2),
        remaining: remaining.toFixed(2),
      };
    });
  }, [receipt.taxIncluded, receipt.taxType, resolvedTaxRate]); // eslint-disable-line react-hooks/exhaustive-deps

  const oilTotal =
    Number(receipt.oilPrice || 0) +
    Number(receipt.extraOilPrice || 0) +
    Number(receipt.oilFilterPrice || 0) +
    (receipt.fullServiceIncluded
      ? Number(receipt.fullServicePrice || FULL_SERVICE_PRICE)
      : 0);

  const handleClearAll = () => {
    setReceipt(receiptInit);
    setIsEditing(false);
    setSelectedCustomer("");
    setSelectedVehicle("");
    setSelectedServices([]);
    setSelectedTires([]);
    setSelectedOil("");
    setSelectedOilFilter(null);
    setSelectedExtraOil(null);
    setExtraOilQty(1);
    setExtraOilUnitPrice(0);
    setOilTechnician([]);
    setselectedStatus("");
    setSelectedPaymentType("");
    setSelectedTireOptions([]);
    setLastMiles(null);
  };
  const servicesPartsProps = {
    TOKEN,
    tab: servicesPartsTab,
    onTabChange: setServicesPartsTab,
    editingKey: servicesPartsEditingKey,
    onEditingKeyChange: setServicesPartsEditingKey,
    selectedServices,
    serviceCatalogOptions: serviceOptions.filter(
      (o) => !o.value?.isCustomTemplate,
    ),
    partsCatalogOptions: partsOptions,
    partsCatalogLoading: partSearchLoading,
    nonInventoryPartsCatalogOptions: nonInventoryPartsOptions,
    nonInventoryPartsCatalogLoading: nonInventoryPartsLoading,
    onSearchParts: searchParts,
    formatPartOptionLabel,
    techniciansOptions,
    vendorOptions: getVendorFieldOptions(),
    errors,
    onAddCatalogService: handleAddCatalogService,
    onAddBlankService: handleAddBlankService,
    onUpdateLine: handleUpdateServiceLine,
    onRemoveLine: handleRemoveServiceLine,
    onAddCatalogPart: handleAddCatalogPart,
    onAddBlankPart: handleAddBlankPart,
    tiresCatalogOptions: tiresOptions,
    selectedTiresList: receipt.tires || [],
    onAddCatalogTire: handleAddCatalogTire,
    onUpdateTireLine: handleUpdateTireLine,
    onRemoveTireLine: handleRemoveTireLine,
    onAddBlankTire: handleAddBlankTire,
    oilsOptions,
    filtersOptions,
    extraOilOptions,
    selectedOil,
    selectedExtraOil,
    selectedOilFilter,
    oilTechnician,
    receiptOil: {
      oil: receipt.oil,
      oilPrice: receipt.oilPrice,
      extraOilSelection: receipt.extraOilSelection,
      extraOilPrice: receipt.extraOilPrice,
      extraOilQuantity: receipt.extraOilQuantity,
      extraOilUnitPrice: receipt.extraOilUnitPrice,
      oilFilter: receipt.oilFilter,
      oilFilterPrice: receipt.oilFilterPrice,
      fullServiceIncluded: receipt.fullServiceIncluded,
      fullServicePrice: receipt.fullServicePrice,
    },
    FULL_SERVICE_PRICE,
    onSelectOil: handleSelectOil,
    onOilPriceOverride: handleOilPriceOverride,
    onSelectExtraOil: handleSelectExtraOil,
    onExtraOilQtyChange: handleExtraOilQtyChange,
    onExtraOilUnitPriceChange: handleExtraOilUnitPriceChange,
    onSelectOilFilter: handleSelectOilFilter,
    onOilFilterPriceOverride: handleOilFilterPriceOverride,
    onToggleFullService: handleToggleFullService,
    onSelectOilTechnician: handleSelectOilTechnician,
    oilChangeAdded: receipt.oilChangeAdded,
    onAddOilChangeRow: handleAddOilChangeRow,
    onRemoveOilChange: handleRemoveOilChangeRow,
  };
  return (
    <div style={styles.pageWrapper}>
      {/* ── Container (matches PageShell Container px) ── */}
      <div style={styles.container}>
        {/* ── Header Paper (matches PageShell gradient Paper) ── */}
        <div style={styles.header}>
          {/* Breadcrumb */}
          <div style={styles.breadcrumb}>
            <span
              style={{
                cursor: "pointer",
                padding: "2px 8px",
                borderRadius: 6,
                transition:
                  "color 0.15s, background-color 0.15s, font-weight 0.15s",
              }}
              onClick={() => navigate("/user/dashboard")}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = TOKEN.primary;
                e.currentTarget.style.fontWeight = "700";
                e.currentTarget.style.backgroundColor = `${TOKEN.primary}14`; // ~8% opacity
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = TOKEN.breadcrumbColor;
                e.currentTarget.style.fontWeight = "400";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <HomeIcon style={{ width: 15, height: 15, marginRight: "3px" }} />
              Home
            </span>
            <span>›</span>
            <span
              style={{
                cursor: "pointer",
                padding: "2px 8px",
                borderRadius: 6,
                transition:
                  "color 0.15s, background-color 0.15s, font-weight 0.15s",
              }}
              onClick={() => navigate("/user/estimates")}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = TOKEN.primary;
                e.currentTarget.style.fontWeight = "700";
                e.currentTarget.style.backgroundColor = `${TOKEN.primary}14`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = TOKEN.breadcrumbColor;
                e.currentTarget.style.fontWeight = "400";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Estimates
            </span>
            <span>›</span>
            <span style={styles.breadcrumbActive}>
              {isEditing ? "Edit RO" : "New RO"}
            </span>
          </div>

          {/* Action row */}
          <div style={styles.actionRow}>
            <button
              style={{
                fontSize: 16,
                color: TOKEN.breadcrumbColor,
                border: "none",
                cursor: "pointer",
                background: "transparent",
                fontWeight: 400,
                transition: "color 0.15s, font-weight 0.15s",
              }}
              type="button"
              onClick={() => navigate("/user/estimates")}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#36589A";
                e.currentTarget.style.fontWeight = "700";
                e.currentTarget.style.backgroundColor = `${TOKEN.primary}14`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#6B7280";
                e.currentTarget.style.fontWeight = "400";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {`<-`}
            </button>
            {isEditing && (
              <span
                style={{
                  fontSize: 12,
                  color: TOKEN.breadcrumbColor,
                  fontFamily: TOKEN.fontFamily,
                }}
              >
                Editing: {receipt.customer?.name || ""}
              </span>
            )}
          </div>
        </div>

        {/* ── Body: left col + right summary col ── */}
        <div style={styles.body}>
          {/* ── LEFT COLUMN ── */}
          <div style={styles.leftCol}>
            {/* Date row */}
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <label style={styles.label}>Date</label>
                <input
                  type="date"
                  style={{ ...styles.input, maxWidth: 180 }}
                  value={formatDateInput(receipt.date)}
                  onChange={(e) =>
                    setReceipt({ ...receipt, date: e.target.value })
                  }
                />
              </div>
            </div>

            {/* Customer + Vehicle */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
              }}
            >
              {/* Customer */}
              <div style={styles.card}>
                <div style={styles.sectionHeader}>
                  <span>👤</span>
                  <span>Customer Information</span>
                </div>
                <div style={styles.formRow}>
                  <label style={styles.label}>Customer</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <Select
                        options={customerOptions}
                        isClearable
                        isLoading={customerSearchLoading}
                        value={selectedCustomer}
                        onChange={handleSelectCustomer}
                        onInputChange={handleCustomerInputChange}
                        filterOption={filterCustomerOption}
                        styles={selectStyles}
                        placeholder="Search by name, phone, or email"
                        noOptionsMessage={() =>
                          customerSearchLoading
                            ? "Searching customers..."
                            : getPhoneFromCustomerSearch()
                              ? "No matching customer. Use Phone to add a new customer."
                              : "No matching customers"
                        }
                      />
                    </div>
                    <button
                      style={styles.outlineBtn}
                      type="button"
                      onClick={openAddCustomerModal}
                    >
                      + New
                    </button>
                    {!selectedCustomer && getPhoneFromCustomerSearch() && (
                      <button
                        style={styles.outlineBtn}
                        type="button"
                        onClick={openAddCustomerModal}
                      >
                        Use Phone
                      </button>
                    )}
                    {selectedCustomer?.value?._id && (
                      <button
                        style={styles.outlineBtn}
                        type="button"
                        onClick={openEditCustomerModal}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {errors.customer && (
                    <span style={styles.errorText}>{errors.customer}</span>
                  )}
                </div>
                <div style={styles.twoCol}>
                  <div>
                    <label style={styles.label}>Phone</label>
                    <input
                      style={styles.input}
                      value={
                        receipt.customer?.phone
                          ? formatPhone(receipt.customer.phone)
                          : ""
                      }
                      readOnly
                      placeholder="e.g. (555) 123-4567"
                    />
                  </div>
                  <div>
                    <label style={styles.label}>Email</label>
                    <input
                      style={styles.input}
                      value={receipt.customer?.email || ""}
                      readOnly
                      placeholder="e.g. customer@email.com"
                    />
                  </div>
                </div>
              </div>

              {/* Vehicle */}
              <div style={styles.card}>
                <div style={styles.sectionHeader}>
                  <span>🚗</span>
                  <span>Vehicle Information</span>
                </div>
                <div style={styles.formRow}>
                  <label style={styles.label}>Vehicle</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <Select
                        options={vehiclesOptions}
                        isClearable
                        isLoading={vehicleSearchLoading}
                        value={selectedVehicle}
                        onChange={handleSelectVehicle}
                        onInputChange={handleVehicleInputChange}
                        styles={selectStyles}
                        placeholder="Search year, make, model, or last 8 of VIN"
                        noOptionsMessage={() => {
                          if (vehicleSearchLoading)
                            return "Searching vehicles...";
                          if (vehicleSearchInput) return "No matching vehicles";
                          if (selectedCustomer?.value?._id)
                            return "No vehicles for this customer";
                          return "Start typing to search vehicles";
                        }}
                      />
                    </div>
                    <button
                      style={styles.outlineBtn}
                      type="button"
                      onClick={openVehicleModal}
                    >
                      + New
                    </button>
                  </div>
                  {errors.vehicle && (
                    <span style={styles.errorText}>{errors.vehicle}</span>
                  )}
                </div>
                <div style={styles.twoCol}>
                  <div>
                    <label style={styles.label}>Year / Make / Model</label>
                    <input
                      style={styles.input}
                      value={
                        receipt.vehicle
                          ? `${receipt.vehicle.year || ""} ${receipt.vehicle.make || ""} ${receipt.vehicle.model || ""}`.trim()
                          : ""
                      }
                      readOnly
                      placeholder="e.g. 2022 Ford F-150"
                    />
                  </div>
                  <div>
                    <label style={styles.label}>VIN</label>
                    <input
                      style={styles.input}
                      value={receipt.vehicle?.vin_number || ""}
                      readOnly
                      placeholder="e.g. 1FTFW1E5XNFA12345"
                    />
                  </div>
                </div>
                <div style={{ ...styles.formRow, marginTop: 16 }}>
                  <label style={styles.label}>
                    Mileage{" "}
                    {lastMiles != null && (
                      <span
                        style={{
                          fontWeight: 400,
                          color: TOKEN.breadcrumbColor,
                          fontSize: 12,
                        }}
                      >
                        (last: {lastMiles.toLocaleString()})
                      </span>
                    )}
                  </label>
                  <input
                    type="number"
                    style={{ ...styles.input, maxWidth: 200 }}
                    value={receipt.miles ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setReceipt({
                        ...receipt,
                        miles: v === "" ? "" : Number(v),
                      });
                    }}
                    min={0}
                    placeholder="e.g. 101000"
                  />
                  {errors.miles && (
                    <span style={styles.errorText}>{errors.miles}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Services & Parts */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 16,
                marginBottom: 24,
              }}
            >
              <div style={styles.card}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <div style={{ ...styles.sectionHeader, marginBottom: 0 }}>
                    <span>🔧</span>
                    <span>Services &amp; Parts</span>
                  </div>
                  <Tooltip title="Fullscreen">
                    <IconButton
                      size="small"
                      onClick={() => setIsServicesModalOpen(true)}
                      sx={{
                        color: TOKEN.primary,
                        border: `1px solid ${TOKEN.primary}`,
                        borderRadius: 1.5,
                      }}
                    >
                      <FullscreenIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                <div style={styles.formRow}>
                  <ServicesPartsInlineTables {...servicesPartsProps} />
                </div>
              </div>

              {/* Technician Notes */}
              <div style={styles.card}>
                <div style={styles.sectionHeader}>
                  <span>📋</span>
                  <span>Technician Notes</span>
                </div>
                <textarea
                  style={styles.textarea}
                  rows={4}
                  value={receipt.techNotes || ""}
                  onChange={(e) =>
                    setReceipt({ ...receipt, techNotes: e.target.value })
                  }
                  placeholder="Add technician notes…"
                  maxLength={1000}
                />
                <div
                  style={{
                    textAlign: "right",
                    fontSize: 12,
                    color: "#9CA3AF",
                    marginTop: 4,
                  }}
                >
                  {(receipt.techNotes || "").length} / 1000
                </div>
              </div>
            </div>
            {/* Discount */}
            <div style={styles.card}>
              <div style={styles.sectionHeader}>
                <span>🏷️</span>
                <span>Discount</span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(140px, 1fr) minmax(120px, 1fr)",
                  gap: 12,
                }}
              >
                <div>
                  <label style={styles.label}>Type</label>
                  <select
                    style={styles.input}
                    value={receipt.discountType || ""}
                    onChange={handleDiscountTypeChange}
                  >
                    <option value="">No discount</option>
                    <option value="Percentage">Percentage</option>
                    <option value="Amount">Dollar amount</option>
                  </select>
                </div>
                <div>
                  <label style={styles.label}>
                    Discount{" "}
                    {receipt.discountType === "Percentage" ? "(%)" : "($)"}
                  </label>
                  <input
                    style={styles.input}
                    type="number"
                    value={receipt.discount ?? ""}
                    onChange={handleDiscount}
                    min="0"
                    max={
                      receipt.discountType === "Percentage" ? "100" : undefined
                    }
                    disabled={!receipt.discountType}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ── RIGHT: RO Summary ── */}
          <div style={styles.rightCol}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryHeader}>
                <span style={styles.summaryHeaderTitle}>RO Summary</span>
                <span style={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>
                  {formatDateMMDDYYYY(receipt.date)}
                </span>
              </div>
              <div style={styles.summaryBody}>
                <div style={{ marginBottom: 14 }}>
                  <div style={styles.summaryLabel}>Customer</div>
                  <div style={styles.summaryValue}>
                    {receipt.customer?.name || "—"}
                  </div>
                  {receipt.customer?.phone && (
                    <div style={styles.summaryMeta}>
                      {formatPhone(receipt.customer.phone)}
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: 14 }}>
                  <div style={styles.summaryLabel}>Vehicle</div>
                  <div style={styles.summaryValue}>
                    {receipt.vehicle
                      ? `${receipt.vehicle.year || ""} ${receipt.vehicle.make || ""} ${receipt.vehicle.model || ""}`.trim() ||
                        "—"
                      : "—"}
                  </div>
                  {receipt.vehicle?.vin_number && (
                    <div style={styles.summaryMeta}>
                      VIN: …{String(receipt.vehicle.vin_number).slice(-8)}
                    </div>
                  )}
                  {receipt.miles > 0 && (
                    <div style={styles.summaryMeta}>
                      Miles: {Number(receipt.miles).toLocaleString()}
                    </div>
                  )}
                </div>
                <hr style={styles.divider} />
                {(receipt.services || [])
                  .filter((s) => s?.value?.type !== "part")
                  .map((svc, idx) => (
                    <div
                      style={styles.lineRow}
                      key={svc?.value?._id || `svc-${idx}`}
                    >
                      <span style={styles.lineLabel}>
                        {svc?.value?.name || "Service"}
                        {Number(svc?.value?.quantity) > 1
                          ? ` x${svc.value.quantity}`
                          : ""}
                      </span>
                      <span style={styles.lineAmount}>
                        ${Number(svc?.value?.total_price || 0).toFixed(2)}
                      </span>
                    </div>
                  ))}
                {(receipt.services || [])
                  .filter(
                    (s) =>
                      s?.value?.type === "part" &&
                      !isIncludedGroupServicePart(s),
                  )
                  .map((part, idx) => (
                    <div
                      style={styles.lineRow}
                      key={part?.value?._id || `part-${idx}`}
                    >
                      <span style={styles.lineLabel}>
                        {part?.value?.name || "Part"}
                        {Number(part?.value?.quantity) > 1
                          ? ` x${part.value.quantity}`
                          : ""}
                      </span>
                      <span style={styles.lineAmount}>
                        ${Number(part?.value?.total_price || 0).toFixed(2)}
                      </span>
                    </div>
                  ))}
                {(receipt.tires || []).map((tire, idx) => (
                  <div style={styles.lineRow} key={tire?.id ?? `tire-${idx}`}>
                    <span style={styles.lineLabel}>
                      {`${tire?.brand || "Tire"}${tire?.size ? ` ${tire.size}` : ""}`}
                      {Number(tire?.quantity) > 1 ? ` x${tire.quantity}` : ""}
                    </span>
                    <span style={styles.lineAmount}>
                      ${Number(tire?.total_price || 0).toFixed(2)}
                    </span>
                  </div>
                ))}
                {oilTotal > 0 && (
                  <div style={styles.lineRow}>
                    <span style={styles.lineLabel}>Oil Change</span>
                    <span style={styles.lineAmount}>
                      ${oilTotal.toFixed(2)}
                    </span>
                  </div>
                )}
                {receipt.taxIncluded && (
                  <>
                    <div style={styles.lineRow}>
                      <span style={styles.lineLabel}>Subtotal</span>
                      <span style={styles.lineAmount}>
                        $
                        {Number(
                          summaryTotals.subtotal || receipt.subTotal || 0,
                        ).toFixed(2)}
                      </span>
                    </div>
                    <div style={styles.lineRow}>
                      <span style={styles.lineLabel}>
                        Tax ({receipt.taxType}%)
                      </span>
                      <span style={styles.lineAmount}>
                        $
                        {Number(summaryTotals.tax || receipt.tax || 0).toFixed(
                          2,
                        )}
                      </span>
                    </div>
                  </>
                )}
                {receipt.discountIncluded && (
                  <div style={styles.lineRow}>
                    <span style={styles.lineLabel}>Discount</span>
                    <span style={{ ...styles.lineAmount, color: "#D97706" }}>
                      -${Number(summaryTotals.discountAmount || 0).toFixed(2)}
                      {receipt.discountType === "Percentage"
                        ? ` (${receipt.discount}%)`
                        : ""}
                    </span>
                  </div>
                )}
                <div
                  style={{
                    paddingTop: 12,
                    borderTop: "2px solid #E5E7EB",
                    marginTop: 4,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: TOKEN.breadcrumbColor,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      marginBottom: 4,
                    }}
                  >
                    ESTIMATED TOTAL
                  </div>
                  <div style={styles.totalAmount}>
                    $
                    {Number(
                      summaryTotals.totalPrice || receipt.totalPrice || 0,
                    ).toFixed(2)}
                  </div>
                </div>
                {Number(receipt.paid) > 0 && (
                  <>
                    <div style={{ ...styles.lineRow, marginTop: 8 }}>
                      <span style={styles.lineLabel}>Paid</span>
                      <span style={styles.lineAmount}>
                        ${Number(receipt.paid || 0).toFixed(2)}
                      </span>
                    </div>
                    <div style={styles.lineRow}>
                      <span style={styles.lineLabel}>Remaining</span>
                      <span
                        style={{
                          ...styles.lineAmount,
                          color:
                            Number(summaryTotals.remaining) > 0
                              ? "#DC2626"
                              : "#16A34A",
                        }}
                      >
                        $
                        {Number(
                          summaryTotals.remaining || receipt.remaining || 0,
                        ).toFixed(2)}
                      </span>
                    </div>
                  </>
                )}
                {receipt.techNotes && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: "8px 10px",
                      background: "#F8FAFC",
                      borderRadius: 8,
                      fontSize: 12,
                      color: TOKEN.breadcrumbColor,
                    }}
                  >
                    <strong style={{ color: TOKEN.labelColor }}>Notes:</strong>{" "}
                    {receipt.techNotes}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Sticky Action Footer ── */}
      <div style={{ ...styles.actionFooter, bottom: footerOffset }}>
        {!isEditing && <button type="button" style={styles.ghostBtn} onClick={handleClearAll}>
          🗑️ Clear All
        </button>}
        <div style={{ flex: 1 }} />
        {!editingInvoice && <button
          type="button"
          title="Save as an estimate"
          style={{
            ...styles.secondaryBtn,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
          disabled={btnLoading || showPaymentModal || showEditReview}
          onClick={(e) => handleSaveReceipt(e, "estimate")}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = `${TOKEN.primary}0d`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#FFFFFF";
          }}
        >
          📄 {btnLoading ? "Saving…" : isEditing ? "Save Changes" : "Save Estimate"}
        </button>}
        <button
          type="button"
          title="Complete sale"
          style={{ ...styles.primaryBtn }}
          disabled={btnLoading || showPaymentModal || showEditReview}
          onClick={(e) => handleSaveReceipt(e, "invoice")}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = TOKEN.primaryHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = TOKEN.primary;
          }}
        >
          🧾 {btnLoading ? "Saving…" : editingInvoice ? "Save Changes" : isEditing ? "Convert to Invoice" : "Save Invoice"}
        </button>
        {isEditing ? (
          <button
            type="button"
            style={styles.dangerBtn}
            disabled={btnLoading}
            onClick={() => navigate(`/user/receipts/receipt-details/${incomingEditReceipt._id}`)}
          >
            Cancel Edit
          </button>
        ) : (
          <button
            type="button"
            style={{
              ...styles.ghostBtn,
              color: "#DC2626",
              borderColor: "#FECACA",
            }}
            onClick={() => navigate("/user/estimates")}
          >
            Cancel
          </button>
        )}
      </div>

      {/* ── Dialogs & Modals ── */}
      <ReceiptEditReviewDialog
        open={showEditReview}
        original={incomingEditReceipt || {}}
        next={{ ...computeReceiptForSave(receipt), receiptType: editingInvoice || pendingEditMode === "invoice" ? "Invoice" : "Quote" }}
        loading={btnLoading}
        error={editSaveError}
        onClose={() => setShowEditReview(false)}
        onConfirm={(details) => handleSaveReceipt({ preventDefault() {} }, pendingEditMode, details)}
      />

      <ReusableFormDialog
        open={isCustomerModelVisible}
        onClose={closeCustomerModal}
        title={
          customerModalMode === "edit" ? "Edit Customer" : "Add New Customer"
        }
        initialValues={prepareCustomerFormValues(
          customer,
          Helpers.authParentId,
        )}
        validationSchema={customerValidationSchema}
        fields={customerFields}
        onSubmit={handleSaveCustomer}
        loading={customerBtnLoading}
      />

      <ReusableFormDialog
        open={isVehicleModelVisible}
        onClose={closeVehicleModal}
        title="Add New Vehicle"
        initialValues={prepareVehicleFormValues(vehicle, Helpers.authParentId)}
        validationSchema={vehicleValidationSchema}
        fields={vehicleFields}
        onSubmit={handleSaveVehicle}
        loading={vehicleBtnLoading}
      />

      {/* Oil Change Modal */}
      {/* Oil Change Modal — MUI Dialog (replaces Bootstrap modal) */}
      <Dialog
        open={isOilChangeModalVisible}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setIsOilChangeModalVisible(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "90vh",
            width: {
              xs: "calc(100% - 32px)",
              sm: "min(500px, calc(100% - 64px))",
            },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            pt: 2,
            px: 3,
            flexShrink: 0,
            background: TOKEN.headerGradient,
          }}
        >
          <Box
            sx={{
              fontWeight: 700,
              color: "#fff",
              fontFamily: TOKEN.fontFamily,
            }}
          >
            Oil Change
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setIsOilChangeModalVisible(false)}
            sx={{ color: "#fff" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Select Oil */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 600, color: TOKEN.labelColor }}
              >
                Select Oil
              </Typography>
              <Select
                options={oilsOptions}
                isClearable
                value={selectedOil}
                onChange={handleSelectOil}
                placeholder="Choose an oil"
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                menuPosition="fixed"
                styles={{
                  ...selectStyles,
                  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
                }}
              />
              {(!oilsOptions || oilsOptions.length === 0) && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.75, display: "block" }}
                >
                  No oils available.
                </Typography>
              )}
            </Box>

            {/* Oil Price */}
            <TextField
              label="Oil Price (per car)"
              type="number"
              size="small"
              inputProps={{ min: 0, step: 0.01 }}
              value={receipt.oilPrice ?? 0}
              onChange={(e) => handleOilPriceOverride(e.target.value)}
              disabled={!receipt.oil}
              helperText={
                !receipt.oil ? "Select an oil to enable price override." : ""
              }
            />

            {/* Extra Oil */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 600, color: TOKEN.labelColor }}
              >
                Extra Oil (per quart)
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                  <Select
                    options={extraOilOptions}
                    isClearable
                    value={selectedExtraOil}
                    onChange={handleSelectExtraOil}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    styles={{
                      ...selectStyles,
                      menuPortal: (base) => ({ ...base, zIndex: 1600 }),
                    }}
                    placeholder="Choose extra oil"
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    flex: "0 0 auto",
                  }}
                >
                  <TextField
                    type="number"
                    size="small"
                    inputProps={{ min: 0 }}
                    value={extraOilQty}
                    onChange={(e) => handleExtraOilQtyChange(e.target.value)}
                    sx={{ width: 70 }}
                  />
                  <Typography sx={{ px: 0.5, color: TOKEN.labelColor }}>
                    ×
                  </Typography>
                  <TextField
                    type="number"
                    size="small"
                    inputProps={{ min: 0, step: 0.01 }}
                    value={extraOilUnitPrice ?? 0}
                    onChange={(e) =>
                      handleExtraOilUnitPriceChange(e.target.value)
                    }
                    disabled={!receipt.extraOilSelection}
                    sx={{ width: 90 }}
                  />
                </Box>
              </Box>
              {!receipt.extraOilSelection && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.75, display: "block" }}
                >
                  Select extra oil to enable price override.
                </Typography>
              )}
            </Box>

            {/* Select Oil Filter */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 600, color: TOKEN.labelColor }}
              >
                Select Oil Filter
              </Typography>
              <Select
                options={filtersOptions}
                isClearable
                value={selectedOilFilter}
                onChange={handleSelectOilFilter}
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                menuPosition="fixed"
                styles={{
                  ...selectStyles,
                  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
                }}
                placeholder="Choose an oil filter"
              />
              {(!filtersOptions || filtersOptions.length === 0) && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.75, display: "block" }}
                >
                  No oil filters available.
                </Typography>
              )}
            </Box>

            {/* Oil Filter Price */}
            <TextField
              label="Oil Filter Price"
              type="number"
              size="small"
              inputProps={{ min: 0, step: 0.01 }}
              value={receipt.oilFilterPrice ?? 0}
              onChange={(e) => handleOilFilterPriceOverride(e.target.value)}
              disabled={!receipt.oilFilter}
              helperText={
                !receipt.oilFilter
                  ? "Select an oil filter to enable price override."
                  : ""
              }
            />

            {/* Full Service Checkbox */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 0.5,
              }}
            >
              <Checkbox
                id="fullServiceIncluded"
                checked={!!receipt.fullServiceIncluded}
                onChange={(e) => handleToggleFullService(e.target.checked)}
                sx={{
                  p: 0,
                  color: TOKEN.primary,
                  "&.Mui-checked": { color: TOKEN.primary },
                }}
              />
              <Typography
                component="label"
                htmlFor="fullServiceIncluded"
                variant="body2"
                sx={{
                  color: TOKEN.labelColor,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                Full Service Included (${FULL_SERVICE_PRICE.toFixed(2)})
              </Typography>
            </Box>

            {/* Select Technician */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 600, color: TOKEN.labelColor }}
              >
                Select Technician
              </Typography>
              <Select
                options={techniciansOptions}
                isClearable
                isMulti
                value={oilTechnician}
                onChange={handleSelectOilTechnician}
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                menuPosition="fixed"
                styles={{
                  ...selectStyles,
                  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
                }}
                placeholder="Choose technician(s)"
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, flexShrink: 0 }}>
          <Button
            onClick={handleCancelOilChange}
            variant="outlined"
            color="error"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveOilChange}
            variant="contained"
            sx={{
              textTransform: "none",
              whiteSpace: "nowrap",
              backgroundColor: TOKEN.primary,
              "&:hover": { backgroundColor: TOKEN.primaryHover },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isServicesModalOpen}
        onClose={() => setIsServicesModalOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: "95vw",
            maxWidth: "1700px",
            height: "90vh",
            maxHeight: "90vh",
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: TOKEN.headerGradient,
            py: 1.5,
            px: 3,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              fontWeight: 700,
              color: "#fff",
              fontFamily: TOKEN.fontFamily,
            }}
          >
            Services &amp; Parts
          </Box>
          <Tooltip title="Exit fullscreen">
            <IconButton
              aria-label="exit fullscreen"
              onClick={() => setIsServicesModalOpen(false)}
              sx={{ color: "#fff" }}
            >
              <FullscreenExitIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        <DialogContent dividers sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>
          <ServicesPartsInlineTables {...servicesPartsProps} />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5, flexShrink: 0 }}>
          <Button
            onClick={() => setIsServicesModalOpen(false)}
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: TOKEN.primary,
              "&:hover": { backgroundColor: TOKEN.primaryHover },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
      <ReusableFormDialog
        open={isAddPartModalVisible}
        onClose={() => setIsAddPartModalVisible(false)}
        title="Add New Special Order Part"
        initialValues={partForm}
        validationSchema={partValidationSchema}
        fields={partFields}
        onSubmit={handleSavePartInline}
        loading={partFormLoading}
      />

      <ReusableFormDialog
        open={isAddTireModalVisible}
        onClose={closeAddTireModal}
        title="Add Tire"
        initialValues={prepareTireFormValues(tireForm, Helpers.authParentId)}
        validationSchema={tireValidationSchema}
        fields={tireFields}
        onSubmit={handleSaveTire}
        loading={tireBtnLoading}
      />
      {/* Payment Modal */}
      {showPaymentModal && (
        <div
          className="modal"
          style={{ display: "block", background: "rgba(0,0,0,0.4)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className="modal-content"
              style={{ borderRadius: TOKEN.cardRadius }}
            >
              <div
                className="modal-header"
                style={{
                  background: TOKEN.headerGradient,
                  borderRadius: `${TOKEN.cardRadius} ${TOKEN.cardRadius} 0 0`,
                }}
              >
                <h5
                  className="modal-title"
                  style={{
                    color: "#fff",
                    fontWeight: 700,
                    fontFamily: TOKEN.fontFamily,
                  }}
                >
                  Add Payment Details
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowPaymentModal(false)}
                />
              </div>
              <div className="modal-body">
                <div className="form-group mb-3">
                  <label style={styles.label}>Amount Due</label>
                  <input
                    type="number"
                    style={styles.input}
                    value={payment.amount_due || ""}
                    readOnly
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="invoice-amount-paid" style={styles.label}>Amount Paid</label>
                  <div className="d-flex gap-2">
                    <input
                      id="invoice-amount-paid"
                      type="number"
                      style={styles.input}
                      value={payment.amount_paid ?? ""}
                      min="0"
                      max={payment.amount_due || undefined}
                      step="0.01"
                      onChange={(e) => updatePaymentRemaining(e.target.value)}
                    />
                    <button
                      type="button"
                      style={styles.outlineBtn}
                      onClick={copyDueToPaid}
                    >
                      Copy Due
                    </button>
                  </div>
                  {paymentErrors.amount_paid && (
                    <span style={styles.errorText}>
                      {paymentErrors.amount_paid}
                    </span>
                  )}
                </div>
                <div className="form-group mb-3">
                  <label style={styles.label}>Amount Remaining</label>
                  <input
                    type="number"
                    style={styles.input}
                    value={payment.amount_remaining || ""}
                    readOnly
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="invoice-payment-type" style={styles.label}>Payment Type</label>
                  <Select
                    inputId="invoice-payment-type"
                    options={paymentTypeOptions}
                    value={
                      paymentTypeOptions.find(
                        (o) => o.value === payment.payment_type,
                      ) || null
                    }
                    onChange={(opt) =>
                      setPayment({ ...payment, payment_type: opt?.value || "" })
                    }
                    isClearable
                    styles={selectStyles}
                  />
                  {paymentErrors.payment_type && (
                    <span style={styles.errorText}>
                      {paymentErrors.payment_type}
                    </span>
                  )}
                </div>
                <div className="form-group mb-3">
                  <label style={styles.label}>Payment Date</label>
                  <input
                    type="date"
                    style={styles.input}
                    value={payment.payment_date || todayStr}
                    onChange={(e) =>
                      setPayment({ ...payment, payment_date: e.target.value })
                    }
                  />
                  {paymentErrors.payment_date && (
                    <span style={styles.errorText}>
                      {paymentErrors.payment_date}
                    </span>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  style={styles.ghostBtn}
                  disabled={paymentSaving}
                  onClick={() => {
                    setShowPaymentModal(false);
                    navigate("/user/receipts");
                  }}
                >
                  Close
                </button>
                <button
                  type="button"
                  style={styles.secondaryBtn}
                  disabled={paymentSaving}
                  onClick={() => handleSavePaymentInline(false)}
                >
                  {paymentSaving ? "Saving..." : Number(payment.amount_paid) === 0 ? "Save Invoice" : "Add Payment"}
                </button>
                <button
                  type="button"
                  style={styles.primaryBtn}
                  disabled={paymentSaving}
                  onClick={() => handleSavePaymentInline(true)}
                >
                  {paymentSaving ? "Saving..." : "Save and Print"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddReceipts;
